import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit3, 
  CheckCircle2, 
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  Info,
  Heart,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import { 
  PRIORITY_LABELS, 
  PRIORITY_LEVELS,
  getTypesForMap,
  getSubtypesForType,
  typeHasSubtypes,
  getTypeLabel,
  getSubtypeLabel
} from '../store/mockData';
import { toast } from 'sonner';

// Comprehensive crypto network / chain list (used by every network select in the crypto form)
const CRYPTO_NETWORKS = [
  { value: 'not-specified', label: 'Not specified' },
  // Layer 1s
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'solana', label: 'Solana' },
  { value: 'bnb', label: 'BNB Smart Chain' },
  { value: 'tron', label: 'TRON' },
  { value: 'ton', label: 'TON' },
  { value: 'avalanche', label: 'Avalanche' },
  { value: 'near', label: 'Near Protocol' },
  { value: 'sui', label: 'Sui' },
  { value: 'cardano', label: 'Cardano' },
  { value: 'aptos', label: 'Aptos' },
  { value: 'polkadot', label: 'Polkadot' },
  { value: 'cosmos', label: 'Cosmos' },
  { value: 'algorand', label: 'Algorand' },
  { value: 'stellar', label: 'Stellar' },
  { value: 'hedera', label: 'Hedera' },
  { value: 'other-l1', label: 'Other' },
  // Layer 2s (Ethereum)
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'base', label: 'Base' },
  { value: 'optimism', label: 'Optimism' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'zksync', label: 'zkSync Era' },
  { value: 'starknet', label: 'Starknet' },
  { value: 'linea', label: 'Linea' },
  { value: 'scroll', label: 'Scroll' },
  { value: 'blast', label: 'Blast' },
  { value: 'other-l2', label: 'Other L2' },
];

// Native token ticker per network (used to auto-fill the Token/Coin field for validators)
const NATIVE_TOKENS = {
  bitcoin: 'BTC', ethereum: 'ETH', solana: 'SOL', bnb: 'BNB', tron: 'TRX',
  ton: 'TON', avalanche: 'AVAX', near: 'NEAR', sui: 'SUI', cardano: 'ADA',
  aptos: 'APT', polkadot: 'DOT', cosmos: 'ATOM', algorand: 'ALGO', stellar: 'XLM',
  hedera: 'HBAR', arbitrum: 'ETH', base: 'ETH', optimism: 'ETH', polygon: 'POL',
  zksync: 'ETH', starknet: 'STRK', linea: 'ETH', scroll: 'ETH', blast: 'ETH',
};

const MapDetail = () => {
  const { mapId } = useParams();
  const location = useLocation();
  const aiPrefillApplied = useRef(false);
  const openEntryDialogRef = useRef(null);
  const {
    getMapById, 
    getEntriesForMap, 
    getMapNotes, 
    updateMapNotes,
    addEntry,
    updateEntry,
    deleteEntry,
    updateMapStatus,
    getMapWishes,
    updateMapWishes,
    contacts,
    getContactById,
    fetchEntriesForMap,
    fetchContacts,
    isAuthenticated,
    loading
  } = useLifely();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch entries for this map when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && mapId && !dataLoaded) {
        try {
          await Promise.all([
            fetchEntriesForMap(mapId),
            fetchContacts()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load map data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, mapId, dataLoaded, fetchEntriesForMap, fetchContacts]);

  // AI Assist: when arriving with prefill state, open the Add-Entry modal pre-filled.
  useEffect(() => {
    const prefill = location.state?.aiPrefill;
    if (prefill && !aiPrefillApplied.current && openEntryDialogRef.current) {
      aiPrefillApplied.current = true;
      // Reuse the load logic to populate the form, then force Add mode (not Edit).
      openEntryDialogRef.current(prefill);
      setEditingEntry(null);
      // Clear router state so a refresh/back doesn't re-open the modal.
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const map = getMapById(mapId);
  const entries = getEntriesForMap(mapId);
  const notes = getMapNotes(mapId);
  const mapWishes = getMapWishes ? getMapWishes(mapId) : null;

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(notes);
  const [isEntryDialogOpen, setIsEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showWishesDisclaimer, setShowWishesDisclaimer] = useState(false);
  
  // Map-level wishes state
  const [isEditingWishes, setIsEditingWishes] = useState(false);
  const [mapWishesContactId, setMapWishesContactId] = useState(mapWishes?.contactId || '');
  const [mapWishesNotes, setMapWishesNotes] = useState(mapWishes?.notes || '');
  
  // Entry-level wishes state
  const [wishesOverride, setWishesOverride] = useState(false);
  const [entryWishesContactId, setEntryWishesContactId] = useState('');
  const [entryWishesNotes, setEntryWishesNotes] = useState('');
  
  // Form state - Common fields
  const [entryLabel, setEntryLabel] = useState('');
  const [entryType, setEntryType] = useState('not-specified');
  const [entrySubtype, setEntrySubtype] = useState('not-specified');
  const [entryLocation, setEntryLocation] = useState('');
  const [entryPriority, setEntryPriority] = useState('medium');
  const [entryNotes, setEntryNotes] = useState('');
  const [entryFirstAction, setEntryFirstAction] = useState('');
  
  // Crypto-specific fields (dynamic by Holding Type)
  const [holdingType, setHoldingType] = useState('not-specified');
  const [cryptoNetwork, setCryptoNetwork] = useState('not-specified');
  const [tokenName, setTokenName] = useState('');
  const [cryptoApproxValue, setCryptoApproxValue] = useState('');
  // Exchange / custodial
  const [exchangePlatform, setExchangePlatform] = useState('');
  const [cryptoAccountEmail, setCryptoAccountEmail] = useState('');
  const [cryptoTwoFactor, setCryptoTwoFactor] = useState('');
  // Self-custody wallets (hardware + software)
  const [hardwareBrand, setHardwareBrand] = useState('not-specified');
  const [networksUsed, setNetworksUsed] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  const [passphraseUsed, setPassphraseUsed] = useState(false);
  const [numAccounts, setNumAccounts] = useState('');
  const [walletApp, setWalletApp] = useState('');
  const [deviceOn, setDeviceOn] = useState('');
  // Validator node
  const [numValidators, setNumValidators] = useState('');
  const [nodeHosting, setNodeHosting] = useState('not-specified');
  const [hostingProvider, setHostingProvider] = useState('');
  const [withdrawalAddress, setWithdrawalAddress] = useState('');
  const [clientSoftware, setClientSoftware] = useState('');
  const [accessNodeInstructions, setAccessNodeInstructions] = useState('');
  const [monitoringAlerts, setMonitoringAlerts] = useState('');
  const [nomineeInstructions, setNomineeInstructions] = useState('');
  // Staking
  const [protocolPlatform, setProtocolPlatform] = useState('');
  const [amountStaked, setAmountStaked] = useState('');
  const [lockupPeriod, setLockupPeriod] = useState('');
  const [walletHolding, setWalletHolding] = useState('');
  const [howToExit, setHowToExit] = useState('');
  // DeFi
  const [defiPositionType, setDefiPositionType] = useState('not-specified');
  const [assetsInvolved, setAssetsInvolved] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [liquidationRisk, setLiquidationRisk] = useState(false);
  // NFT
  const [collectionName, setCollectionName] = useState('');
  const [numItems, setNumItems] = useState('');
  const [marketplace, setMarketplace] = useState('');
  // Multi-signature
  const [signersRequired, setSignersRequired] = useState('');
  const [otherSigners, setOtherSigners] = useState('');
  // Layer 2 / Bridge
  const [bridgeUsed, setBridgeUsed] = useState('');
  const [withdrawalToL1, setWithdrawalToL1] = useState('');

  // Shares-specific fields
  const [accountType, setAccountType] = useState('not-specified');
  const [tickerSymbol, setTickerSymbol] = useState('');
  const [sharesHoldings, setSharesHoldings] = useState('');
  const [broker, setBroker] = useState('');
  const [accountReference, setAccountReference] = useState('');
  const [jointAccount, setJointAccount] = useState(false);

  // Property-specific fields
  const [propertyType, setPropertyType] = useState('not-specified');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyApproxValue, setPropertyApproxValue] = useState('');
  const [ownershipStatus, setOwnershipStatus] = useState('not-specified');
  const [jointOwnershipDetails, setJointOwnershipDetails] = useState('');
  const [councilAuthority, setCouncilAuthority] = useState('');
  const [deedLocation, setDeedLocation] = useState('');
  const [mortgageProvider, setMortgageProvider] = useState('');
  const [keyLocation, setKeyLocation] = useState('');

  // Vehicle-specific fields
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [vehicleOwnershipStatus, setVehicleOwnershipStatus] = useState('not-specified');
  const [vehicleApproxValue, setVehicleApproxValue] = useState('');
  const [financeCompany, setFinanceCompany] = useState('');
  const [sharedOwnership, setSharedOwnership] = useState(false);
  const [storageLocation, setStorageLocation] = useState('');
  const [insuranceDetails, setInsuranceDetails] = useState('');
  const [serviceContact, setServiceContact] = useState('');

  // Pet-specific fields
  const [petBreed, setPetBreed] = useState('');
  const [petDateOfBirth, setPetDateOfBirth] = useState('');
  const [microchipNumber, setMicrochipNumber] = useState('');
  const [vetContact, setVetContact] = useState('');
  const [feedingCareRoutine, setFeedingCareRoutine] = useState('');
  const [verbalArrangements, setVerbalArrangements] = useState(false);
  const [emergencyCaregiver, setEmergencyCaregiver] = useState('');

  // Social-specific fields
  const [platformName, setPlatformName] = useState('');
  const [socialUsername, setSocialUsername] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [socialEmail, setSocialEmail] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('');
  const [paidAccount, setPaidAccount] = useState(false);
  const [socialMonthlyCost, setSocialMonthlyCost] = useState('');
  const [socialAutoRenewalDate, setSocialAutoRenewalDate] = useState('');
  const [accountDisposition, setAccountDisposition] = useState('not-specified');

  // Art-specific fields
  const [artCategory, setArtCategory] = useState('not-specified');
  const [artistCreator, setArtistCreator] = useState('');
  const [yearPeriod, setYearPeriod] = useState('');
  const [artQuantity, setArtQuantity] = useState('');
  const [appraisedValue, setAppraisedValue] = useState('');
  const [appraisalDate, setAppraisalDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [artStorageLocation, setArtStorageLocation] = useState('');
  const [artInsurance, setArtInsurance] = useState('');
  const [provenance, setProvenance] = useState('');
  const [authentication, setAuthentication] = useState('');
  const [intendedDisposition, setIntendedDisposition] = useState('not-specified');

  // Physical Records-specific fields
  const [documentType, setDocumentType] = useState('not-specified');
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [documentFormat, setDocumentFormat] = useState('not-specified');
  const [numberOfCopies, setNumberOfCopies] = useState('');
  const [digitalBackupExists, setDigitalBackupExists] = useState(false);
  const [relatedTo, setRelatedTo] = useState('');
  const [retentionNotes, setRetentionNotes] = useState('');

  // Insurance-specific fields
  const [insuranceType, setInsuranceType] = useState('not-specified');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [policyReference, setPolicyReference] = useState('');
  const [coverageAmount, setCoverageAmount] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [insuranceAutoRenewal, setInsuranceAutoRenewal] = useState(false);
  const [jointNamedParties, setJointNamedParties] = useState('');
  const [beneficiaries, setBeneficiaries] = useState('');
  const [agentContact, setAgentContact] = useState('');
  const [policyDocLocation, setPolicyDocLocation] = useState('');

  // Liabilities-specific fields
  const [lenderName, setLenderName] = useState('');
  const [lenderAccountRef, setLenderAccountRef] = useState('');
  const [totalOwed, setTotalOwed] = useState('');
  const [securedAgainst, setSecuredAgainst] = useState('');
  const [securedAgainstProperty, setSecuredAgainstProperty] = useState(false);
  const [lenderContact, setLenderContact] = useState('');

  // Subscriptions-specific fields
  const [serviceType, setServiceType] = useState('not-specified');
  const [subProvider, setSubProvider] = useState('');
  const [subAccountEmail, setSubAccountEmail] = useState('');
  const [subMonthlyCost, setSubMonthlyCost] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [subAutoRenewal, setSubAutoRenewal] = useState(false);
  const [cancellationInstructions, setCancellationInstructions] = useState('');

  // Business-specific fields
  const [businessType, setBusinessType] = useState('not-specified');
  const [businessRole, setBusinessRole] = useState('');
  const [ownershipPercentage, setOwnershipPercentage] = useState('');
  const [companyRegNumber, setCompanyRegNumber] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [accountantContact, setAccountantContact] = useState('');
  const [otherDirectors, setOtherDirectors] = useState('');
  const [businessBank, setBusinessBank] = useState('');
  const [businessApproxValue, setBusinessApproxValue] = useState('');
  const [keyDocumentsLocation, setKeyDocumentsLocation] = useState('');

  // Sort state
  const [sortBy, setSortBy] = useState('date-newest');

  // Sort options - crypto has an extra option for self-custody
  const sortOptions = useMemo(() => {
    const baseOptions = [
      { value: 'name-asc', label: 'Name A → Z' },
      { value: 'name-desc', label: 'Name Z → A' },
      { value: 'date-oldest', label: 'Date added (oldest first)' },
      { value: 'date-newest', label: 'Date added (newest first)' },
      { value: 'priority-low', label: 'Priority (low → critical)' },
      { value: 'priority-high', label: 'Priority (critical → low)' },
    ];
    
    if (mapId === 'crypto') {
      baseOptions.push({ value: 'self-custody', label: 'Self-custody first' });
    }
    
    return baseOptions;
  }, [mapId]);

  // Sorted entries
  const sortedEntries = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    
    // Priority order for sorting
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    
    return [...entries].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.label || '').localeCompare(b.label || '');
        case 'name-desc':
          return (b.label || '').localeCompare(a.label || '');
        case 'date-oldest':
          return (a.lastUpdated || '').localeCompare(b.lastUpdated || '');
        case 'date-newest':
          return (b.lastUpdated || '').localeCompare(a.lastUpdated || '');
        case 'priority-low':
          return (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
        case 'priority-high':
          return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        case 'self-custody':
          // Self-custody items first, then by name
          if (a.selfCustody && !b.selfCustody) return -1;
          if (!a.selfCustody && b.selfCustody) return 1;
          return (a.label || '').localeCompare(b.label || '');
        default:
          return 0;
      }
    });
  }, [entries, sortBy]);

  // Get types and subtypes for current map
  const types = getTypesForMap(mapId);
  const subtypes = getSubtypesForType(mapId, entryType);
  const hasSubtypes = typeHasSubtypes(mapId, entryType);

  // Reusable <SelectItem> list for every crypto network/chain select
  const networkOptions = CRYPTO_NETWORKS.map((n) => (
    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
  ));

  // Reset subtype when type changes
  const handleTypeChange = (newType) => {
    setEntryType(newType);
    // Reset subtype when type changes
    setEntrySubtype('not-specified');
  };

  if (!map) {
    return (
      <div className="text-center py-12" data-testid="map-not-found">
        <p className="text-muted-foreground">Map not found</p>
        <Link to="/maps" className="text-primary hover:underline mt-2 inline-block">
          Return to Maps
        </Link>
      </div>
    );
  }

  const handleSaveNotes = () => {
    updateMapNotes(mapId, notesValue);
    setIsEditingNotes(false);
    toast.success('Map notes saved');
  };

  const resetForm = () => {
    setEntryLabel('');
    setEntryType('not-specified');
    setEntrySubtype('not-specified');
    setEntryLocation('');
    setEntryPriority('medium');
    setEntryNotes('');
    setEntryFirstAction('');
    // Crypto-specific
    setHoldingType('not-specified');
    setCryptoNetwork('not-specified');
    setTokenName('');
    setCryptoApproxValue('');
    setExchangePlatform('');
    setCryptoAccountEmail('');
    setCryptoTwoFactor('');
    setHardwareBrand('not-specified');
    setNetworksUsed('');
    setDeviceLocation('');
    setPassphraseUsed(false);
    setNumAccounts('');
    setWalletApp('');
    setDeviceOn('');
    setNumValidators('');
    setNodeHosting('not-specified');
    setHostingProvider('');
    setWithdrawalAddress('');
    setClientSoftware('');
    setAccessNodeInstructions('');
    setMonitoringAlerts('');
    setNomineeInstructions('');
    setProtocolPlatform('');
    setAmountStaked('');
    setLockupPeriod('');
    setWalletHolding('');
    setHowToExit('');
    setDefiPositionType('not-specified');
    setAssetsInvolved('');
    setWalletAddress('');
    setLiquidationRisk(false);
    setCollectionName('');
    setNumItems('');
    setMarketplace('');
    setSignersRequired('');
    setOtherSigners('');
    setBridgeUsed('');
    setWithdrawalToL1('');
    // Shares-specific
    setAccountType('not-specified');
    setTickerSymbol('');
    setSharesHoldings('');
    setBroker('');
    setAccountReference('');
    setJointAccount(false);
    // Property-specific
    setPropertyType('not-specified');
    setPropertyAddress('');
    setPropertyApproxValue('');
    setOwnershipStatus('not-specified');
    setJointOwnershipDetails('');
    setCouncilAuthority('');
    setDeedLocation('');
    setMortgageProvider('');
    setKeyLocation('');
    // Vehicle-specific
    setVehicleMake('');
    setVehicleModel('');
    setVehicleYear('');
    setVehicleRegistration('');
    setVehicleOwnershipStatus('not-specified');
    setVehicleApproxValue('');
    setFinanceCompany('');
    setSharedOwnership(false);
    setStorageLocation('');
    setInsuranceDetails('');
    setServiceContact('');
    // Pet-specific
    setPetBreed('');
    setPetDateOfBirth('');
    setMicrochipNumber('');
    setVetContact('');
    setFeedingCareRoutine('');
    setVerbalArrangements(false);
    setEmergencyCaregiver('');
    // Social-specific
    setPlatformName('');
    setSocialUsername('');
    setProfileUrl('');
    setSocialEmail('');
    setTwoFactorMethod('');
    setPaidAccount(false);
    setSocialMonthlyCost('');
    setSocialAutoRenewalDate('');
    setAccountDisposition('not-specified');
    // Art-specific
    setArtCategory('not-specified');
    setArtistCreator('');
    setYearPeriod('');
    setArtQuantity('');
    setAppraisedValue('');
    setAppraisalDate('');
    setPurchasePrice('');
    setArtStorageLocation('');
    setArtInsurance('');
    setProvenance('');
    setAuthentication('');
    setIntendedDisposition('not-specified');
    // Physical Records-specific
    setDocumentType('not-specified');
    setPhysicalLocation('');
    setDocumentFormat('not-specified');
    setNumberOfCopies('');
    setDigitalBackupExists(false);
    setRelatedTo('');
    setRetentionNotes('');
    // Insurance-specific
    setInsuranceType('not-specified');
    setInsuranceProvider('');
    setPolicyReference('');
    setCoverageAmount('');
    setRenewalDate('');
    setInsuranceAutoRenewal(false);
    setJointNamedParties('');
    setBeneficiaries('');
    setAgentContact('');
    setPolicyDocLocation('');
    // Liabilities-specific
    setLenderName('');
    setLenderAccountRef('');
    setTotalOwed('');
    setSecuredAgainst('');
    setSecuredAgainstProperty(false);
    setLenderContact('');
    // Subscriptions-specific
    setServiceType('not-specified');
    setSubProvider('');
    setSubAccountEmail('');
    setSubMonthlyCost('');
    setBillingCycle('monthly');
    setPaymentMethod('');
    setSubAutoRenewal(false);
    setCancellationInstructions('');
    // Business-specific
    setBusinessType('not-specified');
    setBusinessRole('');
    setOwnershipPercentage('');
    setCompanyRegNumber('');
    setRegisteredAddress('');
    setAccountantContact('');
    setOtherDirectors('');
    setBusinessBank('');
    setBusinessApproxValue('');
    setKeyDocumentsLocation('');
    // Entry wishes
    setWishesOverride(false);
    setEntryWishesContactId('');
    setEntryWishesNotes('');
  };

  const handleOpenEntryDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setEntryLabel(entry.label || '');
      setEntryType(entry.type || 'not-specified');
      setEntrySubtype(entry.subtype || 'not-specified');
      setEntryLocation(entry.location || '');
      setEntryPriority(entry.priority || 'medium');
      setEntryNotes(entry.notes || '');
      setEntryFirstAction(entry.firstAction || '');
      // Crypto-specific
      setHoldingType(entry.holdingType || 'not-specified');
      setCryptoNetwork(entry.cryptoNetwork || 'not-specified');
      setTokenName(entry.tokenName || '');
      setCryptoApproxValue(entry.cryptoApproxValue || '');
      setExchangePlatform(entry.exchangePlatform || '');
      setCryptoAccountEmail(entry.cryptoAccountEmail || '');
      setCryptoTwoFactor(entry.cryptoTwoFactor || '');
      setHardwareBrand(entry.hardwareBrand || 'not-specified');
      setNetworksUsed(entry.networksUsed || '');
      setDeviceLocation(entry.deviceLocation || '');
      setPassphraseUsed(entry.passphraseUsed || false);
      setNumAccounts(entry.numAccounts || '');
      setWalletApp(entry.walletApp || '');
      setDeviceOn(entry.deviceOn || '');
      setNumValidators(entry.numValidators || '');
      setNodeHosting(entry.nodeHosting || 'not-specified');
      setHostingProvider(entry.hostingProvider || '');
      setWithdrawalAddress(entry.withdrawalAddress || '');
      setClientSoftware(entry.clientSoftware || '');
      setAccessNodeInstructions(entry.accessNodeInstructions || '');
      setMonitoringAlerts(entry.monitoringAlerts || '');
      setNomineeInstructions(entry.nomineeInstructions || '');
      setProtocolPlatform(entry.protocolPlatform || '');
      setAmountStaked(entry.amountStaked || '');
      setLockupPeriod(entry.lockupPeriod || '');
      setWalletHolding(entry.walletHolding || '');
      setHowToExit(entry.howToExit || '');
      setDefiPositionType(entry.defiPositionType || 'not-specified');
      setAssetsInvolved(entry.assetsInvolved || '');
      setWalletAddress(entry.walletAddress || '');
      setLiquidationRisk(entry.liquidationRisk || false);
      setCollectionName(entry.collectionName || '');
      setNumItems(entry.numItems || '');
      setMarketplace(entry.marketplace || '');
      setSignersRequired(entry.signersRequired || '');
      setOtherSigners(entry.otherSigners || '');
      setBridgeUsed(entry.bridgeUsed || '');
      setWithdrawalToL1(entry.withdrawalToL1 || '');
      // Shares-specific
      setAccountType(entry.accountType || 'not-specified');
      setTickerSymbol(entry.tickerSymbol || '');
      setSharesHoldings(entry.sharesHoldings || '');
      setBroker(entry.broker || '');
      setAccountReference(entry.accountReference || '');
      setJointAccount(entry.jointAccount || false);
      // Property-specific
      setPropertyType(entry.propertyType || 'not-specified');
      setPropertyAddress(entry.propertyAddress || '');
      setPropertyApproxValue(entry.propertyApproxValue || '');
      setOwnershipStatus(entry.ownershipStatus || 'not-specified');
      setJointOwnershipDetails(entry.jointOwnershipDetails || '');
      setCouncilAuthority(entry.councilAuthority || '');
      setDeedLocation(entry.deedLocation || '');
      setMortgageProvider(entry.mortgageProvider || '');
      setKeyLocation(entry.keyLocation || '');
      // Vehicle-specific
      setVehicleMake(entry.vehicleMake || '');
      setVehicleModel(entry.vehicleModel || '');
      setVehicleYear(entry.vehicleYear || '');
      setVehicleRegistration(entry.vehicleRegistration || '');
      setVehicleOwnershipStatus(entry.vehicleOwnershipStatus || 'not-specified');
      setVehicleApproxValue(entry.vehicleApproxValue || '');
      setFinanceCompany(entry.financeCompany || '');
      setSharedOwnership(entry.sharedOwnership || false);
      setStorageLocation(entry.storageLocation || '');
      setInsuranceDetails(entry.insuranceDetails || '');
      setServiceContact(entry.serviceContact || '');
      // Pet-specific
      setPetBreed(entry.petBreed || '');
      setPetDateOfBirth(entry.petDateOfBirth || '');
      setMicrochipNumber(entry.microchipNumber || '');
      setVetContact(entry.vetContact || '');
      setFeedingCareRoutine(entry.feedingCareRoutine || '');
      setVerbalArrangements(entry.verbalArrangements || false);
      setEmergencyCaregiver(entry.emergencyCaregiver || '');
      // Social-specific
      setPlatformName(entry.platformName || '');
      setSocialUsername(entry.socialUsername || '');
      setProfileUrl(entry.profileUrl || '');
      setSocialEmail(entry.socialEmail || '');
      setTwoFactorMethod(entry.twoFactorMethod || '');
      setPaidAccount(entry.paidAccount || false);
      setSocialMonthlyCost(entry.socialMonthlyCost || '');
      setSocialAutoRenewalDate(entry.socialAutoRenewalDate || '');
      setAccountDisposition(entry.accountDisposition || 'not-specified');
      // Art-specific
      setArtCategory(entry.artCategory || 'not-specified');
      setArtistCreator(entry.artistCreator || '');
      setYearPeriod(entry.yearPeriod || '');
      setArtQuantity(entry.artQuantity || '');
      setAppraisedValue(entry.appraisedValue || '');
      setAppraisalDate(entry.appraisalDate || '');
      setPurchasePrice(entry.purchasePrice || '');
      setArtStorageLocation(entry.artStorageLocation || '');
      setArtInsurance(entry.artInsurance || '');
      setProvenance(entry.provenance || '');
      setAuthentication(entry.authentication || '');
      setIntendedDisposition(entry.intendedDisposition || 'not-specified');
      // Physical Records-specific
      setDocumentType(entry.documentType || 'not-specified');
      setPhysicalLocation(entry.physicalLocation || '');
      setDocumentFormat(entry.documentFormat || 'not-specified');
      setNumberOfCopies(entry.numberOfCopies || '');
      setDigitalBackupExists(entry.digitalBackupExists || false);
      setRelatedTo(entry.relatedTo || '');
      setRetentionNotes(entry.retentionNotes || '');
      // Insurance-specific
      setInsuranceType(entry.insuranceType || 'not-specified');
      setInsuranceProvider(entry.insuranceProvider || '');
      setPolicyReference(entry.policyReference || '');
      setCoverageAmount(entry.coverageAmount || '');
      setRenewalDate(entry.renewalDate || '');
      setInsuranceAutoRenewal(entry.insuranceAutoRenewal || false);
      setJointNamedParties(entry.jointNamedParties || '');
      setBeneficiaries(entry.beneficiaries || '');
      setAgentContact(entry.agentContact || '');
      setPolicyDocLocation(entry.policyDocLocation || '');
      // Liabilities-specific
      setLenderName(entry.lenderName || '');
      setLenderAccountRef(entry.lenderAccountRef || '');
      setTotalOwed(entry.totalOwed || '');
      setSecuredAgainst(entry.securedAgainst || '');
      setSecuredAgainstProperty(entry.securedAgainstProperty || false);
      setLenderContact(entry.lenderContact || '');
      // Subscriptions-specific
      setServiceType(entry.serviceType || 'not-specified');
      setSubProvider(entry.subProvider || '');
      setSubAccountEmail(entry.subAccountEmail || '');
      setSubMonthlyCost(entry.subMonthlyCost || '');
      setBillingCycle(entry.billingCycle || 'monthly');
      setPaymentMethod(entry.paymentMethod || '');
      setSubAutoRenewal(entry.subAutoRenewal || false);
      setCancellationInstructions(entry.cancellationInstructions || '');
      // Business-specific
      setBusinessType(entry.businessType || 'not-specified');
      setBusinessRole(entry.businessRole || '');
      setOwnershipPercentage(entry.ownershipPercentage || '');
      setCompanyRegNumber(entry.companyRegNumber || '');
      setRegisteredAddress(entry.registeredAddress || '');
      setAccountantContact(entry.accountantContact || '');
      setOtherDirectors(entry.otherDirectors || '');
      setBusinessBank(entry.businessBank || '');
      setBusinessApproxValue(entry.businessApproxValue || '');
      setKeyDocumentsLocation(entry.keyDocumentsLocation || '');
      // Entry wishes
      setWishesOverride(entry.wishesOverride || false);
      setEntryWishesContactId(entry.wishesContactId || '');
      setEntryWishesNotes(entry.wishesNotes || '');
    } else {
      setEditingEntry(null);
      resetForm();
    }
    setIsEntryDialogOpen(true);
  };
  // Expose the dialog opener to the top-level prefill effect (runs before the early return).
  openEntryDialogRef.current = handleOpenEntryDialog;

  const handleSaveEntry = async () => {
    if (!entryLabel.trim()) {
      toast.error('Entry label is required');
      return;
    }

    const entryData = {
      label: entryLabel,
      type: entryType,
      subtype: entrySubtype,
      location: entryLocation,
      priority: entryPriority,
      notes: entryNotes,
      firstAction: entryFirstAction,
    };

    // Add crypto-specific fields
    if (mapId === 'crypto') {
      entryData.holdingType = holdingType;
      // Derive self-custody from the holding type (keeps the badge + sort working)
      entryData.selfCustody = holdingType === 'self-custody-hardware' || holdingType === 'self-custody-software';
      entryData.cryptoNetwork = cryptoNetwork;
      entryData.tokenName = tokenName;
      entryData.cryptoApproxValue = cryptoApproxValue;
      entryData.exchangePlatform = exchangePlatform;
      entryData.cryptoAccountEmail = cryptoAccountEmail;
      entryData.cryptoTwoFactor = cryptoTwoFactor;
      entryData.hardwareBrand = hardwareBrand;
      entryData.networksUsed = networksUsed;
      entryData.deviceLocation = deviceLocation;
      entryData.passphraseUsed = passphraseUsed;
      entryData.numAccounts = numAccounts;
      entryData.walletApp = walletApp;
      entryData.deviceOn = deviceOn;
      entryData.numValidators = numValidators;
      entryData.nodeHosting = nodeHosting;
      entryData.hostingProvider = hostingProvider;
      entryData.withdrawalAddress = withdrawalAddress;
      entryData.clientSoftware = clientSoftware;
      entryData.accessNodeInstructions = accessNodeInstructions;
      entryData.monitoringAlerts = monitoringAlerts;
      entryData.nomineeInstructions = nomineeInstructions;
      entryData.protocolPlatform = protocolPlatform;
      entryData.amountStaked = amountStaked;
      entryData.lockupPeriod = lockupPeriod;
      entryData.walletHolding = walletHolding;
      entryData.howToExit = howToExit;
      entryData.defiPositionType = defiPositionType;
      entryData.assetsInvolved = assetsInvolved;
      entryData.walletAddress = walletAddress;
      entryData.liquidationRisk = liquidationRisk;
      entryData.collectionName = collectionName;
      entryData.numItems = numItems;
      entryData.marketplace = marketplace;
      entryData.signersRequired = signersRequired;
      entryData.otherSigners = otherSigners;
      entryData.bridgeUsed = bridgeUsed;
      entryData.withdrawalToL1 = withdrawalToL1;
    }

    // Add shares-specific fields
    if (mapId === 'shares') {
      entryData.accountType = accountType;
      entryData.tickerSymbol = tickerSymbol;
      entryData.sharesHoldings = sharesHoldings;
      entryData.broker = broker;
      entryData.accountReference = accountReference;
      entryData.jointAccount = jointAccount;
    }

    // Add property-specific fields
    if (mapId === 'property') {
      entryData.propertyType = propertyType;
      entryData.propertyAddress = propertyAddress;
      entryData.propertyApproxValue = propertyApproxValue;
      entryData.ownershipStatus = ownershipStatus;
      entryData.jointOwnershipDetails = jointOwnershipDetails;
      entryData.councilAuthority = councilAuthority;
      entryData.deedLocation = deedLocation;
      entryData.mortgageProvider = mortgageProvider;
      entryData.keyLocation = keyLocation;
    }

    // Add vehicle-specific fields
    if (mapId === 'vehicles') {
      entryData.vehicleMake = vehicleMake;
      entryData.vehicleModel = vehicleModel;
      entryData.vehicleYear = vehicleYear;
      entryData.vehicleRegistration = vehicleRegistration;
      entryData.vehicleOwnershipStatus = vehicleOwnershipStatus;
      entryData.vehicleApproxValue = vehicleApproxValue;
      entryData.financeCompany = financeCompany;
      entryData.sharedOwnership = sharedOwnership;
      entryData.storageLocation = storageLocation;
      entryData.insuranceDetails = insuranceDetails;
      entryData.serviceContact = serviceContact;
    }

    // Add pet-specific fields
    if (mapId === 'pets') {
      entryData.petBreed = petBreed;
      entryData.petDateOfBirth = petDateOfBirth;
      entryData.microchipNumber = microchipNumber;
      entryData.vetContact = vetContact;
      entryData.feedingCareRoutine = feedingCareRoutine;
      entryData.verbalArrangements = verbalArrangements;
      entryData.emergencyCaregiver = emergencyCaregiver;
    }

    // Add social-specific fields
    if (mapId === 'social') {
      entryData.platformName = platformName;
      entryData.socialUsername = socialUsername;
      entryData.profileUrl = profileUrl;
      entryData.socialEmail = socialEmail;
      entryData.twoFactorMethod = twoFactorMethod;
      entryData.paidAccount = paidAccount;
      entryData.socialMonthlyCost = paidAccount ? socialMonthlyCost : '';
      entryData.socialAutoRenewalDate = paidAccount ? socialAutoRenewalDate : '';
      entryData.accountDisposition = accountDisposition;
    }

    // Add art-specific fields
    if (mapId === 'art') {
      entryData.artCategory = artCategory;
      entryData.artistCreator = artistCreator;
      entryData.yearPeriod = yearPeriod;
      entryData.artQuantity = artQuantity;
      entryData.appraisedValue = appraisedValue;
      entryData.appraisalDate = appraisalDate;
      entryData.purchasePrice = purchasePrice;
      entryData.artStorageLocation = artStorageLocation;
      entryData.artInsurance = artInsurance;
      entryData.provenance = provenance;
      entryData.authentication = authentication;
      entryData.intendedDisposition = intendedDisposition;
    }

    // Add physical records-specific fields
    if (mapId === 'physical') {
      entryData.documentType = documentType;
      entryData.physicalLocation = physicalLocation;
      entryData.documentFormat = documentFormat;
      entryData.numberOfCopies = numberOfCopies;
      entryData.digitalBackupExists = digitalBackupExists;
      entryData.relatedTo = relatedTo;
      entryData.retentionNotes = retentionNotes;
    }

    // Add insurance-specific fields
    if (mapId === 'insurance') {
      entryData.insuranceType = insuranceType;
      entryData.insuranceProvider = insuranceProvider;
      entryData.policyReference = policyReference;
      entryData.coverageAmount = coverageAmount;
      entryData.renewalDate = renewalDate;
      entryData.insuranceAutoRenewal = insuranceAutoRenewal;
      entryData.jointNamedParties = jointNamedParties;
      entryData.beneficiaries = beneficiaries;
      entryData.agentContact = agentContact;
      entryData.policyDocLocation = policyDocLocation;
    }

    // Add liabilities-specific fields
    if (mapId === 'liabilities') {
      entryData.lenderName = lenderName;
      entryData.lenderAccountRef = lenderAccountRef;
      entryData.totalOwed = totalOwed;
      entryData.securedAgainst = securedAgainst;
      entryData.securedAgainstProperty = securedAgainstProperty;
      entryData.lenderContact = lenderContact;
    }

    // Add subscriptions-specific fields
    if (mapId === 'subscriptions') {
      entryData.serviceType = serviceType;
      entryData.subProvider = subProvider;
      entryData.subAccountEmail = subAccountEmail;
      entryData.subMonthlyCost = subMonthlyCost;
      entryData.billingCycle = billingCycle;
      entryData.paymentMethod = paymentMethod;
      entryData.subAutoRenewal = subAutoRenewal;
      entryData.cancellationInstructions = cancellationInstructions;
    }

    // Add business-specific fields
    if (mapId === 'business') {
      entryData.businessType = businessType;
      entryData.businessRole = businessRole;
      entryData.ownershipPercentage = ownershipPercentage;
      entryData.companyRegNumber = companyRegNumber;
      entryData.registeredAddress = registeredAddress;
      entryData.accountantContact = accountantContact;
      entryData.otherDirectors = otherDirectors;
      entryData.businessBank = businessBank;
      entryData.businessApproxValue = businessApproxValue;
      entryData.keyDocumentsLocation = keyDocumentsLocation;
    }

    // Add wishes fields (all maps)
    entryData.wishesOverride = wishesOverride;
    if (wishesOverride) {
      entryData.wishesContactId = entryWishesContactId;
      entryData.wishesContactName = entryWishesContactId ? getContactById(entryWishesContactId)?.name : '';
      entryData.wishesNotes = entryWishesNotes;
    } else {
      entryData.wishesContactId = '';
      entryData.wishesContactName = '';
      entryData.wishesNotes = '';
    }

    try {
      setSaving(true);
      if (editingEntry) {
        await updateEntry(mapId, editingEntry.id, entryData);
        toast.success('Entry updated');
      } else {
        await addEntry(mapId, entryData);
        toast.success('Entry added');
      }
      setIsEntryDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await deleteEntry(mapId, entryId);
      toast.success('Entry removed');
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  const handleMarkAsReviewed = () => {
    updateMapStatus(mapId, 'updated');
    toast.success('Map marked as reviewed');
  };

  const getPriorityClasses = (priority) => {
    switch (priority) {
      case 'critical':
        return 'priority-critical';
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      default:
        return 'priority-low';
    }
  };

  // Show loading state
  if (loading.maps && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid={'map-detail-' + mapId}>
      {/* Back Link & Header */}
      <div className="space-y-4">
        <Link 
          to="/maps" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-maps"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Maps
        </Link>

        <div className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
            {map.name}
          </h1>
          <p className="text-muted-foreground text-base">
            {map.description}
          </p>
        </div>

        {/* Helper Note */}
        <div className="warning-text flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground text-sm">Do not enter passwords, private keys, seed phrases, or credentials.</p>
            <p className="mt-1">{map.helperText}</p>
          </div>
        </div>
      </div>

      {/* Map Notes / Intent Block */}
      <Card className="border border-border" data-testid="map-notes-section">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-medium font-body">
            Notes & Intent
          </CardTitle>
          {!isEditingNotes && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditingNotes(true)}
              data-testid="edit-notes-btn"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditingNotes ? (
            <div className="space-y-4">
              <Textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                placeholder="What would you want someone to understand about this area of your life?"
                className="min-h-[120px] resize-none"
                data-testid="notes-textarea"
              />
              <div className="flex items-center gap-2">
                <Button onClick={handleSaveNotes} data-testid="save-notes-btn">
                  Save Notes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setNotesValue(notes);
                    setIsEditingNotes(false);
                  }}
                  data-testid="cancel-notes-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              {notes ? (
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {notes}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  What would you want someone to understand about this area of your life? Click &quot;Edit&quot; to add notes.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map Default Wishes Section */}
      <Card className="border border-border" data-testid="map-wishes-section">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base font-medium font-body">
              Your Wishes (Non-Binding)
            </CardTitle>
          </div>
          {!isEditingWishes && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setShowWishesDisclaimer(true);
              }}
              data-testid="edit-wishes-btn"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                These wishes express your intent and can help executors and family understand your preferences. 
                They do not replace a legal will or binding beneficiary designations.
              </p>
            </div>
          </div>
          
          {isEditingWishes ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Default intended recipient for all entries in this map</Label>
                <ContactSelector
                  value={mapWishesContactId}
                  onSelect={setMapWishesContactId}
                  placeholder="Select contact..."
                  data-testid="map-wishes-contact"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Notes about your wishes</Label>
                <Textarea
                  value={mapWishesNotes}
                  onChange={(e) => setMapWishesNotes(e.target.value)}
                  placeholder="e.g., Split equally between children, To be managed by spouse..."
                  className="min-h-[80px] resize-none"
                  data-testid="map-wishes-notes"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => {
                    const contactName = mapWishesContactId ? getContactById(mapWishesContactId)?.name : '';
                    updateMapWishes(mapId, {
                      contactId: mapWishesContactId,
                      contactName,
                      notes: mapWishesNotes
                    });
                    setIsEditingWishes(false);
                    toast.success('Wishes saved');
                  }}
                  data-testid="save-wishes-btn"
                >
                  Save Wishes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setMapWishesContactId(mapWishes?.contactId || '');
                    setMapWishesNotes(mapWishes?.notes || '');
                    setIsEditingWishes(false);
                  }}
                  data-testid="cancel-wishes-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {(mapWishes?.contactId || mapWishes?.notes) ? (
                <>
                  {mapWishes.contactName && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Intended for: </span>
                      <span className="font-medium">{mapWishes.contactName}</span>
                    </p>
                  )}
                  {mapWishes.notes && (
                    <p className="text-sm text-foreground whitespace-pre-wrap">{mapWishes.notes}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No default wishes set for this map. Click &quot;Edit&quot; to add your intentions.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wishes Disclaimer Dialog */}
      <Dialog open={showWishesDisclaimer} onOpenChange={setShowWishesDisclaimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Important Legal Notice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Lifely is a personal information organizer, not a legal service. 
              The wishes and intentions you record here are for <strong>informational purposes only</strong> and do not constitute:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>A legal will or testament</li>
              <li>A binding beneficiary designation</li>
              <li>Legal or financial advice</li>
              <li>A substitute for proper estate planning</li>
            </ul>
            <p className="text-sm text-muted-foreground">
              To ensure your wishes are legally binding, consult a qualified estate planning attorney in your jurisdiction.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowWishesDisclaimer(false);
                setIsEditingWishes(true);
              }}
              data-testid="acknowledge-disclaimer-btn"
            >
              I Understand, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entries Section */}
      <section className="space-y-4" data-testid="entries-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-heading font-medium text-foreground">
            Entries ({entries.length})
          </h2>
          <div className="flex items-center gap-2">
            {entries.length > 1 && (
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="sort-entries-select">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value} className="text-xs">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={() => handleOpenEntryDialog()} data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        </div>

        {entries.length === 0 ? (
          <Card className="border border-dashed border-border bg-muted/30">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No entries yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add your first entry to start mapping this area.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedEntries.map((entry) => (
              <Card 
                key={entry.id} 
                className="card-hover border border-border"
                data-testid={'entry-card-' + entry.id}
              >
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-foreground truncate">
                          {entry.label}
                        </h3>
                        {/* Crypto: Self-custody badge */}
                        {mapId === 'crypto' && entry.selfCustody && (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                            Self-custody
                          </Badge>
                        )}
                        {/* Vehicle: Shared ownership badge */}
                        {mapId === 'vehicles' && entry.sharedOwnership && (
                          <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                            Shared
                          </Badge>
                        )}
                        {/* Pet: Verbal arrangements badge */}
                        {mapId === 'pets' && entry.verbalArrangements && (
                          <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                            Verbal arrangements
                          </Badge>
                        )}
                        {/* Social: Account disposition badge */}
                        {mapId === 'social' && entry.accountDisposition && entry.accountDisposition !== 'not-specified' && (
                          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                            {entry.accountDisposition === 'memorialize' ? 'Memorialize' :
                             entry.accountDisposition === 'archive' ? 'Keep active' :
                             entry.accountDisposition === 'close' ? 'Close/Delete' :
                             entry.accountDisposition === 'transfer' ? 'Transfer' : 'Other'}
                          </Badge>
                        )}
                      </div>
                      {/* Social: Platform name */}
                      {mapId === 'social' && entry.platformName && (
                        <p className="text-xs text-primary">
                          {entry.platformName}
                        </p>
                      )}
                      {/* Social: handle / URL */}
                      {mapId === 'social' && (entry.socialUsername || entry.profileUrl) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {entry.socialUsername || entry.profileUrl}
                        </p>
                      )}
                      {/* Art: Creator and year */}
                      {mapId === 'art' && (entry.artistCreator || entry.yearPeriod) && (
                        <p className="text-xs text-primary">
                          {[entry.artistCreator, entry.yearPeriod].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* Art: Quantity */}
                      {mapId === 'art' && entry.artQuantity && (
                        <p className="text-xs text-muted-foreground">
                          Qty: {entry.artQuantity}
                        </p>
                      )}
                      {/* Art: Appraised value */}
                      {mapId === 'art' && entry.appraisedValue && (
                        <p className="text-xs text-muted-foreground">
                          Value: <span className="font-medium">{entry.appraisedValue}</span>
                        </p>
                      )}
                      {/* Shares: Ticker Symbol */}
                      {mapId === 'shares' && entry.tickerSymbol && (
                        <p className="text-xs font-mono text-primary">
                          {entry.tickerSymbol}
                        </p>
                      )}
                      {/* Vehicle: Make/Model/Year */}
                      {mapId === 'vehicles' && (entry.vehicleMake || entry.vehicleModel || entry.vehicleYear) && (
                        <p className="text-xs text-primary">
                          {[entry.vehicleYear, entry.vehicleMake, entry.vehicleModel].filter(Boolean).join(' ')}
                        </p>
                      )}
                      {/* Pet: Breed */}
                      {mapId === 'pets' && entry.petBreed && (
                        <p className="text-xs text-primary">
                          {entry.petBreed}
                        </p>
                      )}
                      {/* Property: Address */}
                      {mapId === 'property' && entry.propertyAddress && (
                        <p className="text-xs text-muted-foreground">
                          {entry.propertyAddress}
                        </p>
                      )}
                      {/* Insurance: Provider */}
                      {mapId === 'insurance' && entry.insuranceProvider && (
                        <p className="text-xs text-primary">
                          {entry.insuranceProvider}
                        </p>
                      )}
                      {/* Insurance: Coverage amount */}
                      {mapId === 'insurance' && entry.coverageAmount && (
                        <p className="text-xs text-muted-foreground">
                          Coverage: <span className="font-medium">{entry.coverageAmount}</span>
                        </p>
                      )}
                      {/* Physical: Location */}
                      {mapId === 'physical' && entry.physicalLocation && (
                        <p className="text-xs text-muted-foreground">
                          📍 {entry.physicalLocation}
                        </p>
                      )}
                      {/* Physical: Document format badge */}
                      {mapId === 'physical' && entry.documentFormat && entry.documentFormat !== 'not-specified' && (
                        <Badge variant="outline" className="text-xs">
                          {entry.documentFormat === 'original' ? 'Original' :
                           entry.documentFormat === 'certified-copy' ? 'Certified copy' :
                           entry.documentFormat === 'photocopy' ? 'Photocopy' :
                           entry.documentFormat === 'digital-scan' ? 'Digital scan' :
                           entry.documentFormat === 'both' ? 'Physical + Digital' : entry.documentFormat}
                        </Badge>
                      )}
                      {entry.type && entry.type !== 'not-specified' && (
                        <p className="text-xs text-primary">
                          {getTypeLabel(mapId, entry.type)}
                          {entry.subtype && entry.subtype !== 'not-specified' && (
                            <span className="text-muted-foreground"> / {getSubtypeLabel(mapId, entry.type, entry.subtype)}</span>
                          )}
                        </p>
                      )}
                      {/* Generic location - only show if not property/vehicle/pet/social/art/physical/insurance specific */}
                      {entry.location && !['property', 'vehicles', 'pets', 'social', 'art', 'physical', 'insurance'].includes(mapId) && (
                        <p className="text-sm text-muted-foreground truncate">
                          {entry.location}
                        </p>
                      )}
                      {/* Vehicle: Storage location */}
                      {mapId === 'vehicles' && entry.storageLocation && (
                        <p className="text-xs text-muted-foreground truncate">
                          📍 {entry.storageLocation}
                        </p>
                      )}
                      {/* Art: Storage location */}
                      {mapId === 'art' && entry.artStorageLocation && (
                        <p className="text-xs text-muted-foreground truncate">
                          📍 {entry.artStorageLocation}
                        </p>
                      )}
                      {/* Pet: Emergency caregiver */}
                      {mapId === 'pets' && entry.emergencyCaregiver && (
                        <p className="text-xs text-muted-foreground truncate">
                          Emergency: {entry.emergencyCaregiver}
                        </p>
                      )}
                      {/* Insurance: Renewal date */}
                      {mapId === 'insurance' && entry.renewalDate && (
                        <p className="text-xs text-muted-foreground truncate">
                          Renews: {entry.renewalDate}
                        </p>
                      )}
                      {/* Insurance: Beneficiaries */}
                      {mapId === 'insurance' && entry.beneficiaries && (
                        <p className="text-xs text-muted-foreground truncate">
                          Beneficiary: {entry.beneficiaries}
                        </p>
                      )}
                      {/* Liabilities: Lender name */}
                      {mapId === 'liabilities' && entry.lenderName && (
                        <p className="text-xs text-primary">
                          {entry.lenderName}
                        </p>
                      )}
                      {/* Liabilities: Approximate total owed */}
                      {mapId === 'liabilities' && entry.totalOwed && (
                        <p className="text-xs text-muted-foreground">
                          Owed: <span className="font-medium">{entry.totalOwed}</span>
                        </p>
                      )}
                      {/* Physical: Related to */}
                      {mapId === 'physical' && entry.relatedTo && (
                        <p className="text-xs text-muted-foreground truncate">
                          Related: {entry.relatedTo}
                        </p>
                      )}
                      {/* Shares: Approximate holdings */}
                      {mapId === 'shares' && entry.sharesHoldings && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">{entry.sharesHoldings}</span>
                        </p>
                      )}
                    </div>
                    <Badge className={'text-xs ' + getPriorityClasses(entry.priority)}>
                      {PRIORITY_LABELS[entry.priority]}
                    </Badge>
                  </div>

                  {entry.notes && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {entry.notes}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Updated: {entry.lastUpdated}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenEntryDialog(entry)}
                        data-testid={'edit-entry-' + entry.id}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={'delete-entry-' + entry.id}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Entry Dialog */}
      <Dialog open={isEntryDialogOpen} onOpenChange={setIsEntryDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="entry-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 space-y-1 border-b border-border">
            <DialogTitle className="font-heading text-base">
              {editingEntry ? 'Edit Entry' : 'Add Entry'}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Document what exists and where it&apos;s managed, not credentials or secrets.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
            <div className="md:col-span-2 flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-amber-500" />
              <span><strong className="font-medium text-foreground">Lifely is not a vault.</strong> Don&apos;t enter passwords, private keys, seed phrases, or recovery codes.</span>
            </div>

            {/* Entry Label - Dynamic label based on map type */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="entry-label">
                {mapId === 'shares' ? 'Investment Name / Label *' : 
                 mapId === 'property' ? 'Property Name / Label *' :
                 mapId === 'vehicles' ? 'Vehicle Name / Label *' :
                 mapId === 'pets' ? 'Pet Name / Identifier *' :
                 mapId === 'social' ? 'Account Name / Username *' :
                 mapId === 'art' ? 'Item Name / Title *' :
                 mapId === 'physical' ? 'Document / Record Name *' :
                 mapId === 'insurance' ? 'Policy Name / Description *' :
                 mapId === 'liabilities' ? 'Debt / Obligation Name *' :
                 'Entry Label *'}
              </Label>
              <Input
                id="entry-label"
                value={entryLabel}
                onChange={(e) => setEntryLabel(e.target.value)}
                placeholder={
                  mapId === 'crypto' ? (
                    holdingType === 'exchange' ? 'e.g. My Coinbase Account' :
                    holdingType === 'self-custody-hardware' ? 'e.g. My Ledger Nano X' :
                    holdingType === 'self-custody-software' ? 'e.g. MetaMask - Main Wallet' :
                    holdingType === 'validator' ? 'e.g. Ethereum Validator #1' :
                    holdingType === 'staking' ? 'e.g. ETH Staking via Lido' :
                    holdingType === 'defi' ? 'e.g. Aave USDC Position' :
                    holdingType === 'nft' ? 'e.g. My NFT Collection' :
                    holdingType === 'multisig' ? 'e.g. Company Gnosis Safe' :
                    holdingType === 'layer2' ? 'e.g. Arbitrum Bridge Position' :
                    'e.g. My Bitcoin Holdings'
                  ) :
                  mapId === 'shares' ? 'e.g., Apple Inc., Vanguard S&P 500 ETF' :
                  mapId === 'property' ? 'e.g., Family Home, Beach House, Rental Property' :
                  mapId === 'vehicles' ? 'e.g., Daily Driver, Classic Mustang, Family Boat' :
                  mapId === 'pets' ? 'e.g., Max, Bella, Mr. Whiskers' :
                  mapId === 'social' ? 'e.g., @johndoe, john.doe@email.com' :
                  mapId === 'art' ? 'e.g., Sunset over Mountains, Grandmother\'s Ring, 1965 Rolex Submariner' :
                  mapId === 'physical' ? 'e.g., Birth Certificate, Property Deed, Tax Returns 2020-2023' :
                  mapId === 'insurance' ? 'e.g., Home Insurance, Term Life Policy, Auto Insurance' :
                  mapId === 'liabilities' ? 'e.g., Home Mortgage, Car Loan, Personal Loan' :
                  'e.g., Primary Savings Account'
                }
                data-testid="entry-label-input"
              />
            </div>

            {/* Type (hidden for maps with a dedicated category select) */}
            {!['insurance', 'art', 'physical', 'crypto', 'subscriptions', 'business'].includes(mapId) && (
              <div className="space-y-2">
                <Label htmlFor="entry-type">Type</Label>
                <Select value={entryType} onValueChange={handleTypeChange}>
                  <SelectTrigger id="entry-type" data-testid="entry-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subtype - only show if type has subtypes */}
            {!['insurance', 'art', 'physical', 'crypto', 'subscriptions', 'business'].includes(mapId) && hasSubtypes && subtypes.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="entry-subtype">Subtype</Label>
                <Select value={entrySubtype} onValueChange={setEntrySubtype}>
                  <SelectTrigger id="entry-subtype" data-testid="entry-subtype-select">
                    <SelectValue placeholder="Select subtype" />
                  </SelectTrigger>
                  <SelectContent>
                    {subtypes.map((subtype) => (
                      <SelectItem key={subtype.value} value={subtype.value}>
                        {subtype.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ===================== CRYPTO ===================== */}
            {mapId === 'crypto' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="holding-type">Holding Type</Label>
                <Select value={holdingType} onValueChange={setHoldingType}>
                  <SelectTrigger id="holding-type" data-testid="holding-type-select">
                    <SelectValue placeholder="Select holding type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="exchange">Exchange / Custodial Account</SelectItem>
                    <SelectItem value="self-custody-hardware">Self-Custody Wallet (Hardware)</SelectItem>
                    <SelectItem value="self-custody-software">Self-Custody Wallet (Software/Hot)</SelectItem>
                    <SelectItem value="validator">Validator Node</SelectItem>
                    <SelectItem value="staking">Staking Position</SelectItem>
                    <SelectItem value="defi">DeFi Position (Lending/Liquidity)</SelectItem>
                    <SelectItem value="nft">NFT Collection</SelectItem>
                    <SelectItem value="multisig">Multi-signature Wallet</SelectItem>
                    <SelectItem value="layer2">Layer 2 / Bridge Position</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Validator wind-down warning */}
            {mapId === 'crypto' && holdingType === 'validator' && (
              <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Validator nodes require specific wind-down procedures. Make sure your nominee knows to seek technical help before taking any action. Pulling a validator offline incorrectly can result in slashing penalties.
                </p>
              </div>
            )}

            {/* --- Exchange / Custodial Account --- */}
            {mapId === 'crypto' && holdingType === 'exchange' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="exchange-platform">Exchange / Platform</Label>
                  <Input
                    id="exchange-platform"
                    value={exchangePlatform}
                    onChange={(e) => setExchangePlatform(e.target.value)}
                    placeholder="e.g. Coinbase, Binance, Kraken"
                    data-testid="exchange-platform-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-account-email">Account Email</Label>
                  <Input
                    id="crypto-account-email"
                    value={cryptoAccountEmail}
                    onChange={(e) => setCryptoAccountEmail(e.target.value)}
                    placeholder="Email used to register"
                    data-testid="crypto-account-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Total Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Chain</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-two-factor">2FA Method</Label>
                  <Input
                    id="crypto-two-factor"
                    value={cryptoTwoFactor}
                    onChange={(e) => setCryptoTwoFactor(e.target.value)}
                    placeholder="e.g. Google Authenticator, SMS"
                    data-testid="crypto-two-factor-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-xs text-muted-foreground italic">
                    Recovery and access details (seed phrases, PINs, keys, access info) will be supported via secure encrypted storage in a future update, please don&apos;t enter them here for now.
                  </p>
                </div>
              </>
            )}

            {/* --- Self-Custody Wallet (Hardware) --- */}
            {mapId === 'crypto' && holdingType === 'self-custody-hardware' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="hardware-brand">Hardware Wallet Brand</Label>
                  <Select value={hardwareBrand} onValueChange={setHardwareBrand}>
                    <SelectTrigger id="hardware-brand" data-testid="hardware-brand-select">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not specified</SelectItem>
                      <SelectItem value="ledger">Ledger</SelectItem>
                      <SelectItem value="trezor">Trezor</SelectItem>
                      <SelectItem value="coldcard">Coldcard</SelectItem>
                      <SelectItem value="bitbox">BitBox</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="networks-used">Networks / Chains Used</Label>
                  <Input
                    id="networks-used"
                    value={networksUsed}
                    onChange={(e) => setNetworksUsed(e.target.value)}
                    placeholder="e.g. Bitcoin, Ethereum, Solana"
                    data-testid="networks-used-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Total Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device-location">Device Location</Label>
                  <Input
                    id="device-location"
                    value={deviceLocation}
                    onChange={(e) => setDeviceLocation(e.target.value)}
                    placeholder="e.g. Home office safe"
                    data-testid="device-location-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-xs text-muted-foreground italic">
                    Seed phrase and PIN locations will be supported via secure encrypted storage in a future update, please don&apos;t enter them here for now.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="num-accounts">Number of Accounts / Wallets on Device</Label>
                  <Input
                    id="num-accounts"
                    value={numAccounts}
                    onChange={(e) => setNumAccounts(e.target.value)}
                    placeholder="e.g. 3"
                    data-testid="num-accounts-input"
                  />
                </div>
                <div className="md:col-span-2 flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                  <Checkbox
                    id="passphrase-used"
                    checked={passphraseUsed}
                    onCheckedChange={setPassphraseUsed}
                    data-testid="passphrase-used-checkbox"
                  />
                  <div className="space-y-1 leading-none">
                    <Label htmlFor="passphrase-used" className="font-medium cursor-pointer">
                      Passphrase used (25th word)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      An extra passphrase is required in addition to the seed phrase. Your nominee must know this exists or funds are unrecoverable.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* --- Self-Custody Wallet (Software / Hot) --- */}
            {mapId === 'crypto' && holdingType === 'self-custody-software' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="wallet-app">Wallet Name / App</Label>
                  <Input
                    id="wallet-app"
                    value={walletApp}
                    onChange={(e) => setWalletApp(e.target.value)}
                    placeholder="e.g. MetaMask, Phantom, Trust Wallet"
                    data-testid="wallet-app-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="networks-used">Networks / Chains Used</Label>
                  <Input
                    id="networks-used"
                    value={networksUsed}
                    onChange={(e) => setNetworksUsed(e.target.value)}
                    placeholder="e.g. Ethereum, Polygon, Solana"
                    data-testid="networks-used-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Total Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device-on">Device it&apos;s on</Label>
                  <Input
                    id="device-on"
                    value={deviceOn}
                    onChange={(e) => setDeviceOn(e.target.value)}
                    placeholder="e.g. MacBook Pro, iPhone"
                    data-testid="device-on-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-xs text-muted-foreground italic">
                    Seed phrase location will be supported via secure encrypted storage in a future update, please don&apos;t enter it here for now.
                  </p>
                </div>
                <div className="md:col-span-2 flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                  <Checkbox
                    id="passphrase-used"
                    checked={passphraseUsed}
                    onCheckedChange={setPassphraseUsed}
                    data-testid="passphrase-used-checkbox"
                  />
                  <div className="space-y-1 leading-none">
                    <Label htmlFor="passphrase-used" className="font-medium cursor-pointer">
                      Passphrase used (25th word)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      An extra passphrase is required in addition to the seed phrase. Your nominee must know this exists or funds are unrecoverable.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* --- Validator Node --- */}
            {mapId === 'crypto' && holdingType === 'validator' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Blockchain</Label>
                  <Select value={cryptoNetwork} onValueChange={(v) => { setCryptoNetwork(v); if (NATIVE_TOKENS[v]) setTokenName(NATIVE_TOKENS[v]); }}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="num-validators">Number of Validators</Label>
                  <Input
                    id="num-validators"
                    value={numValidators}
                    onChange={(e) => setNumValidators(e.target.value)}
                    placeholder="e.g. 2"
                    data-testid="num-validators-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Value Staked</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="e.g. 32 ETH per validator"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="node-hosting">Node Hosting</Label>
                  <Select value={nodeHosting} onValueChange={setNodeHosting}>
                    <SelectTrigger id="node-hosting" data-testid="node-hosting-select">
                      <SelectValue placeholder="Select hosting" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not specified</SelectItem>
                      <SelectItem value="self-hosted">Self-hosted</SelectItem>
                      <SelectItem value="cloud">Cloud (AWS/GCP/etc)</SelectItem>
                      <SelectItem value="third-party">Third-party service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hosting-provider">Hosting Provider / Location</Label>
                  <Input
                    id="hosting-provider"
                    value={hostingProvider}
                    onChange={(e) => setHostingProvider(e.target.value)}
                    placeholder='e.g. "Home server, rack in garage" or "AWS eu-west-1"'
                    data-testid="hosting-provider-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdrawal-address">Withdrawal Address</Label>
                  <Input
                    id="withdrawal-address"
                    value={withdrawalAddress}
                    onChange={(e) => setWithdrawalAddress(e.target.value)}
                    placeholder="Where funds go when unstaked"
                    data-testid="withdrawal-address-input"
                  />
                  <p className="text-xs text-muted-foreground">The Ethereum address where staking rewards and withdrawn ETH will be sent</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-software">Client Software</Label>
                  <Input
                    id="client-software"
                    value={clientSoftware}
                    onChange={(e) => setClientSoftware(e.target.value)}
                    placeholder="e.g. Geth + Lighthouse"
                    data-testid="client-software-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monitoring-alerts">Monitoring / Alerts</Label>
                  <Input
                    id="monitoring-alerts"
                    value={monitoringAlerts}
                    onChange={(e) => setMonitoringAlerts(e.target.value)}
                    placeholder="e.g. beaconcha.in alerts to email@..."
                    data-testid="monitoring-alerts-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="access-node-instructions">How to Access Node</Label>
                  <Textarea
                    id="access-node-instructions"
                    value={accessNodeInstructions}
                    onChange={(e) => setAccessNodeInstructions(e.target.value)}
                    placeholder="e.g. SSH to IP address, credentials in..."
                    className="min-h-[60px] resize-none"
                    data-testid="access-node-instructions-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="nominee-instructions">Exit / Wind-down Instructions</Label>
                  <Textarea
                    id="nominee-instructions"
                    value={nomineeInstructions}
                    onChange={(e) => setNomineeInstructions(e.target.value)}
                    placeholder="Step by step notes for your nominee or executor. Who to contact, what NOT to do, how to initiate voluntary exit."
                    className="min-h-[60px] resize-none"
                    data-testid="nominee-instructions-input"
                  />
                </div>
              </>
            )}

            {/* --- Staking Position --- */}
            {mapId === 'crypto' && holdingType === 'staking' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Protocol</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="protocol-platform">Protocol / Platform</Label>
                  <Input
                    id="protocol-platform"
                    value={protocolPlatform}
                    onChange={(e) => setProtocolPlatform(e.target.value)}
                    placeholder='e.g. "Lido Finance", "Rocket Pool", "Native Solana staking"'
                    data-testid="protocol-platform-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount-staked">Approximate Amount Staked</Label>
                  <Input
                    id="amount-staked"
                    value={amountStaked}
                    onChange={(e) => setAmountStaked(e.target.value)}
                    placeholder="e.g. 10 ETH"
                    data-testid="amount-staked-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lockup-period">Lockup / Unbonding Period</Label>
                  <Input
                    id="lockup-period"
                    value={lockupPeriod}
                    onChange={(e) => setLockupPeriod(e.target.value)}
                    placeholder="e.g. 21 days to unstake"
                    data-testid="lockup-period-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-holding">Wallet Holding Position</Label>
                  <Input
                    id="wallet-holding"
                    value={walletHolding}
                    onChange={(e) => setWalletHolding(e.target.value)}
                    placeholder="e.g. MetaMask - seed phrase location..."
                    data-testid="wallet-holding-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="how-to-exit">How to Unstake</Label>
                  <Textarea
                    id="how-to-exit"
                    value={howToExit}
                    onChange={(e) => setHowToExit(e.target.value)}
                    placeholder="Steps to unstake / unbond and where funds arrive"
                    className="min-h-[60px] resize-none"
                    data-testid="how-to-exit-input"
                  />
                </div>
              </>
            )}

            {/* --- DeFi Position (Lending / Liquidity) --- */}
            {mapId === 'crypto' && holdingType === 'defi' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="protocol-platform">Protocol</Label>
                  <Input
                    id="protocol-platform"
                    value={protocolPlatform}
                    onChange={(e) => setProtocolPlatform(e.target.value)}
                    placeholder='e.g. "Aave v3", "Uniswap v3", "Compound"'
                    data-testid="protocol-platform-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Chain</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="defi-position-type">Position Type</Label>
                  <Select value={defiPositionType} onValueChange={setDefiPositionType}>
                    <SelectTrigger id="defi-position-type" data-testid="defi-position-type-select">
                      <SelectValue placeholder="Select position type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not specified</SelectItem>
                      <SelectItem value="lending">Lending / Supplying</SelectItem>
                      <SelectItem value="borrowing">Borrowing</SelectItem>
                      <SelectItem value="liquidity-pool">Liquidity Pool</SelectItem>
                      <SelectItem value="yield-farm">Yield Farm</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assets-involved">Assets Involved</Label>
                  <Input
                    id="assets-involved"
                    value={assetsInvolved}
                    onChange={(e) => setAssetsInvolved(e.target.value)}
                    placeholder="e.g. ETH/USDC pool"
                    data-testid="assets-involved-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-address">Wallet Address</Label>
                  <Input
                    id="wallet-address"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Public address only - for nominee to look up"
                    data-testid="wallet-address-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="how-to-exit">How to Access / Exit</Label>
                  <Textarea
                    id="how-to-exit"
                    value={howToExit}
                    onChange={(e) => setHowToExit(e.target.value)}
                    placeholder="e.g. Connect wallet at app.aave.com, click withdraw"
                    className="min-h-[60px] resize-none"
                    data-testid="how-to-exit-input"
                  />
                </div>
                <div className="md:col-span-2 flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                  <Checkbox
                    id="liquidation-risk"
                    checked={liquidationRisk}
                    onCheckedChange={setLiquidationRisk}
                    data-testid="liquidation-risk-checkbox"
                  />
                  <div className="space-y-1 leading-none">
                    <Label htmlFor="liquidation-risk" className="font-medium cursor-pointer">
                      Liquidation risk
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      There is active borrowing that could be liquidated if not managed in time.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* --- NFT Collection --- */}
            {mapId === 'crypto' && holdingType === 'nft' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="collection-name">Collection Name</Label>
                  <Input
                    id="collection-name"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                    placeholder="e.g. Bored Ape Yacht Club"
                    data-testid="collection-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Chain</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="num-items">Number of Items</Label>
                  <Input
                    id="num-items"
                    value={numItems}
                    onChange={(e) => setNumItems(e.target.value)}
                    placeholder="e.g. 5"
                    data-testid="num-items-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Floor Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="marketplace">Marketplace</Label>
                  <Input
                    id="marketplace"
                    value={marketplace}
                    onChange={(e) => setMarketplace(e.target.value)}
                    placeholder="e.g. OpenSea, Magic Eden"
                    data-testid="marketplace-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-holding">Wallet Holding NFTs</Label>
                  <Input
                    id="wallet-holding"
                    value={walletHolding}
                    onChange={(e) => setWalletHolding(e.target.value)}
                    placeholder="Wallet address or description"
                    data-testid="wallet-holding-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-xs text-muted-foreground italic">
                    Seed phrase / access location will be supported via secure encrypted storage in a future update, please don&apos;t enter it here for now.
                  </p>
                </div>
              </>
            )}

            {/* --- Multi-signature Wallet --- */}
            {mapId === 'crypto' && holdingType === 'multisig' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="protocol-platform">Protocol</Label>
                  <Input
                    id="protocol-platform"
                    value={protocolPlatform}
                    onChange={(e) => setProtocolPlatform(e.target.value)}
                    placeholder='e.g. "Gnosis Safe", "Squads"'
                    data-testid="protocol-platform-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network / Chain</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signers-required">Number of Signers Required</Label>
                  <Input
                    id="signers-required"
                    value={signersRequired}
                    onChange={(e) => setSignersRequired(e.target.value)}
                    placeholder="e.g. 2 of 3"
                    data-testid="signers-required-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-address">Wallet Address</Label>
                  <Input
                    id="wallet-address"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Public address"
                    data-testid="wallet-address-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-xs text-muted-foreground italic">
                    Your signing key / seed location will be supported via secure encrypted storage in a future update, please don&apos;t enter it here for now.
                  </p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="other-signers">Other Signers</Label>
                  <Textarea
                    id="other-signers"
                    value={otherSigners}
                    onChange={(e) => setOtherSigners(e.target.value)}
                    placeholder="Names and contact details"
                    className="min-h-[60px] resize-none"
                    data-testid="other-signers-input"
                  />
                </div>
              </>
            )}

            {/* --- Layer 2 / Bridge Position --- */}
            {mapId === 'crypto' && holdingType === 'layer2' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="crypto-network">Network</Label>
                  <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                    <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent>{networkOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bridge-used">Bridge Used</Label>
                  <Input
                    id="bridge-used"
                    value={bridgeUsed}
                    onChange={(e) => setBridgeUsed(e.target.value)}
                    placeholder='e.g. "Arbitrum Bridge", "Hop Protocol"'
                    data-testid="bridge-used-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crypto-approx-value">Approximate Value</Label>
                  <Input
                    id="crypto-approx-value"
                    value={cryptoApproxValue}
                    onChange={(e) => setCryptoApproxValue(e.target.value)}
                    placeholder="Rough estimate"
                    data-testid="crypto-approx-value-input"
                  />
                  <p className="text-xs text-muted-foreground">Rough estimate only, do not store exact balances</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-address">Wallet Address</Label>
                  <Input
                    id="wallet-address"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Public address"
                    data-testid="wallet-address-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="withdrawal-to-l1">Withdrawal to L1</Label>
                  <Textarea
                    id="withdrawal-to-l1"
                    value={withdrawalToL1}
                    onChange={(e) => setWithdrawalToL1(e.target.value)}
                    placeholder="How to bridge back to mainnet"
                    className="min-h-[60px] resize-none"
                    data-testid="withdrawal-to-l1-input"
                  />
                </div>
              </>
            )}

            {/* Crypto: general network select (only when type unset or other) */}
            {mapId === 'crypto' && (holdingType === 'not-specified' || holdingType === 'other') && (
              <div className="space-y-2">
                <Label htmlFor="crypto-network">Network / Blockchain</Label>
                <Select value={cryptoNetwork} onValueChange={setCryptoNetwork}>
                  <SelectTrigger id="crypto-network" data-testid="crypto-network-select">
                    <SelectValue placeholder="Select network" />
                  </SelectTrigger>
                  <SelectContent>{networkOptions}</SelectContent>
                </Select>
              </div>
            )}

            {/* Crypto: Token / Coin (always shown) */}
            {mapId === 'crypto' && (
              <div className="space-y-2">
                <Label htmlFor="token-name">Token / Coin</Label>
                <Input
                  id="token-name"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="e.g. Bitcoin, ETH, USDC"
                  data-testid="token-name-input"
                />
              </div>
            )}

            {/* ===================== SHARES ===================== */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="account-type">Account Type</Label>
                <Select value={accountType} onValueChange={setAccountType}>
                  <SelectTrigger id="account-type" data-testid="account-type-select">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="isa">ISA</SelectItem>
                    <SelectItem value="sipp-pension">SIPP / Pension</SelectItem>
                    <SelectItem value="gia">GIA</SelectItem>
                    <SelectItem value="401k">401k</SelectItem>
                    <SelectItem value="brokerage">Brokerage</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="ticker-symbol">Ticker Symbol / ISIN</Label>
                <Input
                  id="ticker-symbol"
                  value={tickerSymbol}
                  onChange={(e) => setTickerSymbol(e.target.value)}
                  placeholder="e.g., AAPL, MSFT, VOO, GOOGL, US0378331005"
                  data-testid="ticker-symbol-input"
                />
              </div>
            )}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="shares-holdings">Approximate Holdings</Label>
                <Input
                  id="shares-holdings"
                  value={sharesHoldings}
                  onChange={(e) => setSharesHoldings(e.target.value)}
                  placeholder="e.g. 100 shares, £5000 approx"
                  data-testid="shares-holdings-input"
                />
              </div>
            )}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="account-reference">Account Reference</Label>
                <Input
                  id="account-reference"
                  value={accountReference}
                  onChange={(e) => setAccountReference(e.target.value)}
                  placeholder="e.g., Account ending in 1234"
                  data-testid="account-reference-input"
                />
              </div>
            )}
            {mapId === 'shares' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="joint-account"
                  checked={jointAccount}
                  onCheckedChange={setJointAccount}
                  data-testid="joint-account-checkbox"
                />
                <Label htmlFor="joint-account" className="text-sm font-normal cursor-pointer">
                  Joint account
                </Label>
              </div>
            )}

            {/* ===================== PROPERTY ===================== */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="property-type">Property Type</Label>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger id="property-type" data-testid="property-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                    <SelectItem value="overseas">Overseas</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="property-address">Property Address</Label>
                <Input
                  id="property-address"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  placeholder="e.g., 123 Main Street, City, State, ZIP"
                  data-testid="property-address-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="property-approx-value">Approximate Value</Label>
                <Input
                  id="property-approx-value"
                  value={propertyApproxValue}
                  onChange={(e) => setPropertyApproxValue(e.target.value)}
                  placeholder="Rough estimate"
                  data-testid="property-approx-value-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="ownership-status">Ownership Status</Label>
                <Select value={ownershipStatus} onValueChange={setOwnershipStatus}>
                  <SelectTrigger id="ownership-status" data-testid="ownership-status-select">
                    <SelectValue placeholder="Select ownership status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="owned-outright">Owned outright</SelectItem>
                    <SelectItem value="mortgaged">Mortgaged</SelectItem>
                    <SelectItem value="joint-ownership">Joint ownership</SelectItem>
                    <SelectItem value="leasehold">Leasehold</SelectItem>
                    <SelectItem value="rented-to-tenants">Rented to tenants</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="joint-ownership-details">Joint Ownership Details</Label>
                <Input
                  id="joint-ownership-details"
                  value={jointOwnershipDetails}
                  onChange={(e) => setJointOwnershipDetails(e.target.value)}
                  placeholder="e.g. Joint with spouse, 50/50"
                  data-testid="joint-ownership-details-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="council-authority">Council / Local Authority</Label>
                <Input
                  id="council-authority"
                  value={councilAuthority}
                  onChange={(e) => setCouncilAuthority(e.target.value)}
                  placeholder="e.g., Westminster City Council"
                  data-testid="council-authority-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="deed-location">Deed / Title Location</Label>
                <Input
                  id="deed-location"
                  value={deedLocation}
                  onChange={(e) => setDeedLocation(e.target.value)}
                  placeholder="e.g., Safe deposit box at First National Bank, With solicitor"
                  data-testid="deed-location-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="mortgage-provider">Mortgage Provider</Label>
                <Input
                  id="mortgage-provider"
                  value={mortgageProvider}
                  onChange={(e) => setMortgageProvider(e.target.value)}
                  placeholder="e.g., Wells Fargo, Nationwide Building Society"
                  data-testid="mortgage-provider-input"
                />
              </div>
            )}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="key-location">Key Location</Label>
                <Input
                  id="key-location"
                  value={keyLocation}
                  onChange={(e) => setKeyLocation(e.target.value)}
                  placeholder="e.g. Hook by front door, spare with neighbour"
                  data-testid="key-location-input"
                />
              </div>
            )}
            {mapId === 'property' && ownershipStatus === 'mortgaged' && (
              <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  This property is mortgaged. Consider adding the mortgage details to your{' '}
                  <Link to="/maps/liabilities" className="font-medium underline" onClick={() => setIsEntryDialogOpen(false)}>
                    Liabilities map
                  </Link>{' '}
                  to track the outstanding balance and lender details.
                </p>
              </div>
            )}

            {/* ===================== VEHICLES ===================== */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-make">Make</Label>
                <Input
                  id="vehicle-make"
                  value={vehicleMake}
                  onChange={(e) => setVehicleMake(e.target.value)}
                  placeholder="e.g., Ford, Toyota, Porsche, Beneteau"
                  data-testid="vehicle-make-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-model">Model</Label>
                <Input
                  id="vehicle-model"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  placeholder="e.g., Mustang GT, Camry, 911 Carrera, Oceanis 40"
                  data-testid="vehicle-model-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-year">Year</Label>
                <Input
                  id="vehicle-year"
                  value={vehicleYear}
                  onChange={(e) => setVehicleYear(e.target.value)}
                  placeholder="e.g., 2022, 1967"
                  data-testid="vehicle-year-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-registration">Registration / License Plate</Label>
                <Input
                  id="vehicle-registration"
                  value={vehicleRegistration}
                  onChange={(e) => setVehicleRegistration(e.target.value)}
                  placeholder="e.g., ABC 123, Boat registration number"
                  data-testid="vehicle-registration-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-ownership-status">Ownership Status</Label>
                <Select value={vehicleOwnershipStatus} onValueChange={setVehicleOwnershipStatus}>
                  <SelectTrigger id="vehicle-ownership-status" data-testid="vehicle-ownership-status-select">
                    <SelectValue placeholder="Select ownership status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="owned-outright">Owned outright</SelectItem>
                    <SelectItem value="financed">Financed</SelectItem>
                    <SelectItem value="leased">Leased</SelectItem>
                    <SelectItem value="shared-ownership">Shared ownership / Syndicate</SelectItem>
                    <SelectItem value="investment-vehicle">Investment vehicle</SelectItem>
                    <SelectItem value="company-owned">Company owned</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-approx-value">Approximate Value</Label>
                <Input
                  id="vehicle-approx-value"
                  value={vehicleApproxValue}
                  onChange={(e) => setVehicleApproxValue(e.target.value)}
                  placeholder="Rough estimate"
                  data-testid="vehicle-approx-value-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="finance-company">Finance Company</Label>
                <Input
                  id="finance-company"
                  value={financeCompany}
                  onChange={(e) => setFinanceCompany(e.target.value)}
                  placeholder="If financed or on lease"
                  data-testid="finance-company-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="storage-location">Storage Location</Label>
                <Input
                  id="storage-location"
                  value={storageLocation}
                  onChange={(e) => setStorageLocation(e.target.value)}
                  placeholder="e.g., Home garage, Classic car storage, Marina berth A-15, Hangar 7"
                  data-testid="storage-location-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="insurance-details">Insurance Details</Label>
                <Input
                  id="insurance-details"
                  value={insuranceDetails}
                  onChange={(e) => setInsuranceDetails(e.target.value)}
                  placeholder="e.g., State Farm, Policy through Hagerty for classic coverage"
                  data-testid="insurance-details-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="service-contact">Service & Maintenance Contact</Label>
                <Input
                  id="service-contact"
                  value={serviceContact}
                  onChange={(e) => setServiceContact(e.target.value)}
                  placeholder="e.g., Joe's Auto Shop, Marina service department"
                  data-testid="service-contact-input"
                />
              </div>
            )}
            {mapId === 'vehicles' && (
              <div className="md:col-span-2 flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="shared-ownership"
                  checked={sharedOwnership}
                  onCheckedChange={setSharedOwnership}
                  data-testid="shared-ownership-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="shared-ownership" className="font-medium cursor-pointer">
                    Shared ownership or verbal arrangements
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This vehicle has shared ownership, syndicate arrangements, or verbal agreements (classic car clubs, race car partnerships, etc.)
                  </p>
                </div>
              </div>
            )}
            {mapId === 'vehicles' && (vehicleOwnershipStatus === 'financed' || vehicleOwnershipStatus === 'leased') && (
              <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  This vehicle is financed or leased. Consider adding the finance details to your{' '}
                  <Link to="/maps/liabilities" className="font-medium underline" onClick={() => setIsEntryDialogOpen(false)}>
                    Liabilities map
                  </Link>{' '}
                  to track the outstanding balance and finance terms.
                </p>
              </div>
            )}

            {/* ===================== INSURANCE ===================== */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="insurance-type">Insurance Type</Label>
                <Select value={insuranceType} onValueChange={setInsuranceType}>
                  <SelectTrigger id="insurance-type" data-testid="insurance-type-select">
                    <SelectValue placeholder="Select insurance type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="life">Life</SelectItem>
                    <SelectItem value="health">Health / Medical</SelectItem>
                    <SelectItem value="home-contents">Home & Contents</SelectItem>
                    <SelectItem value="vehicle">Car / Vehicle</SelectItem>
                    <SelectItem value="income-protection">Income Protection</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="insurance-provider">Insurance Provider</Label>
                <Input
                  id="insurance-provider"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  placeholder="e.g., State Farm, Allstate, MetLife, Prudential"
                  data-testid="insurance-provider-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="policy-reference">Policy Reference</Label>
                <Input
                  id="policy-reference"
                  value={policyReference}
                  onChange={(e) => setPolicyReference(e.target.value)}
                  placeholder="e.g., Policy ending in 4521, Group policy through employer"
                  data-testid="policy-reference-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="coverage-amount">Coverage Amount</Label>
                <Input
                  id="coverage-amount"
                  value={coverageAmount}
                  onChange={(e) => setCoverageAmount(e.target.value)}
                  placeholder="e.g., $500,000, £250,000 death benefit, $100k/$300k liability"
                  data-testid="coverage-amount-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="renewal-date">Renewal Date</Label>
                <Input
                  id="renewal-date"
                  value={renewalDate}
                  onChange={(e) => setRenewalDate(e.target.value)}
                  placeholder="e.g., March 15 annually, Auto-renews, Term ends 2035"
                  data-testid="renewal-date-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="joint-named-parties">Joint / Named Parties</Label>
                <Input
                  id="joint-named-parties"
                  value={jointNamedParties}
                  onChange={(e) => setJointNamedParties(e.target.value)}
                  placeholder="Other people covered by this policy"
                  data-testid="joint-named-parties-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="beneficiaries">Beneficiaries</Label>
                <Input
                  id="beneficiaries"
                  value={beneficiaries}
                  onChange={(e) => setBeneficiaries(e.target.value)}
                  placeholder="e.g., Spouse primary, Children contingent, Estate"
                  data-testid="beneficiaries-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="agent-contact">Agent / Broker Contact</Label>
                <Input
                  id="agent-contact"
                  value={agentContact}
                  onChange={(e) => setAgentContact(e.target.value)}
                  placeholder="e.g., John Smith, ABC Insurance Agency (555-1234)"
                  data-testid="agent-contact-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="policy-doc-location">Policy Documents Location</Label>
                <Input
                  id="policy-doc-location"
                  value={policyDocLocation}
                  onChange={(e) => setPolicyDocLocation(e.target.value)}
                  placeholder="e.g., Filing cabinet, Email archive, Online portal"
                  data-testid="policy-doc-location-input"
                />
              </div>
            )}
            {mapId === 'insurance' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="insurance-auto-renewal"
                  checked={insuranceAutoRenewal}
                  onCheckedChange={setInsuranceAutoRenewal}
                  data-testid="insurance-auto-renewal-checkbox"
                />
                <Label htmlFor="insurance-auto-renewal" className="text-sm font-normal cursor-pointer">
                  Auto-renewal
                </Label>
              </div>
            )}

            {/* ===================== LIABILITIES ===================== */}
            {mapId === 'liabilities' && (
              <div className="space-y-2">
                <Label htmlFor="lender-name">Lender / Creditor</Label>
                <Input
                  id="lender-name"
                  value={lenderName}
                  onChange={(e) => setLenderName(e.target.value)}
                  placeholder="e.g., Westpac, ANZ, Commonwealth Bank, Dad"
                  data-testid="lender-name-input"
                />
              </div>
            )}
            {mapId === 'liabilities' && (
              <div className="space-y-2">
                <Label htmlFor="lender-account-ref">Account / Loan Reference</Label>
                <Input
                  id="lender-account-ref"
                  value={lenderAccountRef}
                  onChange={(e) => setLenderAccountRef(e.target.value)}
                  placeholder="e.g., Loan ending in 4521, Mortgage account ref"
                  data-testid="lender-account-ref-input"
                />
              </div>
            )}
            {mapId === 'liabilities' && (
              <div className="space-y-2">
                <Label htmlFor="total-owed">Approximate Total Owed</Label>
                <Input
                  id="total-owed"
                  value={totalOwed}
                  onChange={(e) => setTotalOwed(e.target.value)}
                  placeholder="Rough current balance"
                  data-testid="total-owed-input"
                />
              </div>
            )}
            {mapId === 'liabilities' && (
              <div className="space-y-2">
                <Label htmlFor="secured-against">Secured Against</Label>
                <Input
                  id="secured-against"
                  value={securedAgainst}
                  onChange={(e) => setSecuredAgainst(e.target.value)}
                  placeholder="e.g. Property address if mortgage"
                  data-testid="secured-against-input"
                />
              </div>
            )}
            {mapId === 'liabilities' && (
              <div className="space-y-2">
                <Label htmlFor="lender-contact">Lender Contact</Label>
                <Input
                  id="lender-contact"
                  value={lenderContact}
                  onChange={(e) => setLenderContact(e.target.value)}
                  placeholder="e.g., ANZ Home Loans 1300 XXX XXX, Local branch manager Jane"
                  data-testid="lender-contact-input"
                />
              </div>
            )}
            {mapId === 'liabilities' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="secured-against-property"
                  checked={securedAgainstProperty}
                  onCheckedChange={setSecuredAgainstProperty}
                  data-testid="secured-against-property-checkbox"
                />
                <Label htmlFor="secured-against-property" className="text-sm font-normal cursor-pointer">
                  Secured against property
                </Label>
              </div>
            )}

            {/* ===================== ART / COLLECTIBLES ===================== */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-category">Category</Label>
                <Select value={artCategory} onValueChange={setArtCategory}>
                  <SelectTrigger id="art-category" data-testid="art-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="art">Art</SelectItem>
                    <SelectItem value="jewellery">Jewellery</SelectItem>
                    <SelectItem value="watches">Watches</SelectItem>
                    <SelectItem value="wine-spirits">Wine / Spirits</SelectItem>
                    <SelectItem value="collectibles">Collectibles</SelectItem>
                    <SelectItem value="antiques">Antiques</SelectItem>
                    <SelectItem value="coins-stamps">Coins / Stamps</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="artist-creator">Creator / Artist / Maker</Label>
                <Input
                  id="artist-creator"
                  value={artistCreator}
                  onChange={(e) => setArtistCreator(e.target.value)}
                  placeholder="e.g., Claude Monet, Tiffany & Co., Rolex, Unknown craftsman"
                  data-testid="artist-creator-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="year-period">Year / Period</Label>
                <Input
                  id="year-period"
                  value={yearPeriod}
                  onChange={(e) => setYearPeriod(e.target.value)}
                  placeholder="e.g., 1889, Victorian era, 1960s, Circa 1920"
                  data-testid="year-period-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-quantity">Quantity</Label>
                <Input
                  id="art-quantity"
                  value={artQuantity}
                  onChange={(e) => setArtQuantity(e.target.value)}
                  placeholder="Number of items if a collection"
                  data-testid="art-quantity-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="appraised-value">Appraised Value</Label>
                <Input
                  id="appraised-value"
                  value={appraisedValue}
                  onChange={(e) => setAppraisedValue(e.target.value)}
                  placeholder="e.g., $15,000, £8,500, Estimated $5,000-7,000"
                  data-testid="appraised-value-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="appraisal-date">Appraisal Date</Label>
                <Input
                  id="appraisal-date"
                  value={appraisalDate}
                  onChange={(e) => setAppraisalDate(e.target.value)}
                  placeholder="e.g., March 2023, Never appraised"
                  data-testid="appraisal-date-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="purchase-price">Purchase Price</Label>
                <Input
                  id="purchase-price"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  placeholder="Original purchase price if known"
                  data-testid="purchase-price-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-storage-location">Storage Location</Label>
                <Input
                  id="art-storage-location"
                  value={artStorageLocation}
                  onChange={(e) => setArtStorageLocation(e.target.value)}
                  placeholder="e.g., Displayed in living room, Bank safe deposit box, Climate-controlled storage"
                  data-testid="art-storage-location-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-insurance">Insurance Details</Label>
                <Input
                  id="art-insurance"
                  value={artInsurance}
                  onChange={(e) => setArtInsurance(e.target.value)}
                  placeholder="e.g., Covered under home policy, Separate rider with Chubb, Not insured"
                  data-testid="art-insurance-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="provenance">Provenance / History</Label>
                <Textarea
                  id="provenance"
                  value={provenance}
                  onChange={(e) => setProvenance(e.target.value)}
                  placeholder="e.g., Purchased at Christie's 2015, Inherited from grandmother, Family heirloom since 1920"
                  className="min-h-[60px] resize-none"
                  data-testid="provenance-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="authentication">Authentication / Documentation</Label>
                <Textarea
                  id="authentication"
                  value={authentication}
                  onChange={(e) => setAuthentication(e.target.value)}
                  placeholder="e.g., Certificate of authenticity from gallery, GIA certificate #12345, Original receipt in safe"
                  className="min-h-[60px] resize-none"
                  data-testid="authentication-input"
                />
              </div>
            )}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="intended-disposition">Intended Disposition</Label>
                <Select value={intendedDisposition} onValueChange={setIntendedDisposition}>
                  <SelectTrigger id="intended-disposition" data-testid="intended-disposition-select">
                    <SelectValue placeholder="What should happen to this item?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="keep-in-family">Keep in family</SelectItem>
                    <SelectItem value="gift-to-specific">Gift to specific person</SelectItem>
                    <SelectItem value="sell-auction">Sell at auction</SelectItem>
                    <SelectItem value="sell-dealer">Sell through dealer</SelectItem>
                    <SelectItem value="donate-museum">Donate to museum/institution</SelectItem>
                    <SelectItem value="donate-charity">Donate to charity</SelectItem>
                    <SelectItem value="other">Other (explain in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ===================== PETS ===================== */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="pet-breed">Breed / Species</Label>
                <Input
                  id="pet-breed"
                  value={petBreed}
                  onChange={(e) => setPetBreed(e.target.value)}
                  placeholder="e.g., Golden Retriever, Domestic Shorthair, Cockatiel"
                  data-testid="pet-breed-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="pet-dob">Date of Birth / Age</Label>
                <Input
                  id="pet-dob"
                  value={petDateOfBirth}
                  onChange={(e) => setPetDateOfBirth(e.target.value)}
                  placeholder="e.g., March 2019, ~5 years old"
                  data-testid="pet-dob-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="microchip-number">Microchip Number</Label>
                <Input
                  id="microchip-number"
                  value={microchipNumber}
                  onChange={(e) => setMicrochipNumber(e.target.value)}
                  placeholder="If applicable"
                  data-testid="microchip-number-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="vet-contact">Veterinary / Care Contact</Label>
                <Input
                  id="vet-contact"
                  value={vetContact}
                  onChange={(e) => setVetContact(e.target.value)}
                  placeholder="e.g., Happy Paws Veterinary Clinic, Dr. Sarah Johnson"
                  data-testid="vet-contact-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="emergency-caregiver">Emergency / Alternate Caregiver</Label>
                <Input
                  id="emergency-caregiver"
                  value={emergencyCaregiver}
                  onChange={(e) => setEmergencyCaregiver(e.target.value)}
                  placeholder="e.g., Sister Jane (555-1234), Neighbor Bob has spare key"
                  data-testid="emergency-caregiver-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="feeding-care-routine">Feeding & Care Routine</Label>
                <Textarea
                  id="feeding-care-routine"
                  value={feedingCareRoutine}
                  onChange={(e) => setFeedingCareRoutine(e.target.value)}
                  placeholder="Daily routine, dietary needs, medications"
                  className="min-h-[60px] resize-none"
                  data-testid="feeding-care-routine-input"
                />
              </div>
            )}
            {mapId === 'pets' && (
              <div className="md:col-span-2 flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="verbal-arrangements"
                  checked={verbalArrangements}
                  onCheckedChange={setVerbalArrangements}
                  data-testid="verbal-arrangements-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="verbal-arrangements" className="font-medium cursor-pointer">
                    Verbal arrangements
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This animal&apos;s care or ownership includes informal or verbal agreements (not documented in writing)
                  </p>
                </div>
              </div>
            )}

            {/* ===================== PHYSICAL RECORDS ===================== */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="document-type">Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger id="document-type" data-testid="document-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="will">Will / Testament</SelectItem>
                    <SelectItem value="property-deed">Property Deed</SelectItem>
                    <SelectItem value="birth-certificate">Birth Certificate</SelectItem>
                    <SelectItem value="marriage-certificate">Marriage Certificate</SelectItem>
                    <SelectItem value="passport-id">Passport / ID</SelectItem>
                    <SelectItem value="financial-records">Financial Records</SelectItem>
                    <SelectItem value="medical-records">Medical Records</SelectItem>
                    <SelectItem value="insurance-documents">Insurance Documents</SelectItem>
                    <SelectItem value="business-records">Business Records</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="physical-location">Physical Location</Label>
                <Input
                  id="physical-location"
                  value={physicalLocation}
                  onChange={(e) => setPhysicalLocation(e.target.value)}
                  placeholder="e.g., Filing cabinet in home office, Safe deposit box at bank, Home safe"
                  data-testid="physical-location-input"
                />
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="document-format">Document Format</Label>
                <Select value={documentFormat} onValueChange={setDocumentFormat}>
                  <SelectTrigger id="document-format" data-testid="document-format-select">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="original">Original document</SelectItem>
                    <SelectItem value="certified-copy">Certified copy</SelectItem>
                    <SelectItem value="photocopy">Photocopy</SelectItem>
                    <SelectItem value="digital-scan">Digital scan</SelectItem>
                    <SelectItem value="both">Both physical and digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="number-of-copies">Number of Copies</Label>
                <Input
                  id="number-of-copies"
                  value={numberOfCopies}
                  onChange={(e) => setNumberOfCopies(e.target.value)}
                  placeholder="How many copies exist"
                  data-testid="number-of-copies-input"
                />
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2 md:col-span-2">
                <p className="text-xs text-muted-foreground italic">
                  Access details (who holds keys, combinations) will be supported via secure encrypted storage in a future update, please don&apos;t enter them here for now.
                </p>
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="related-to">Related To (asset/property)</Label>
                <Input
                  id="related-to"
                  value={relatedTo}
                  onChange={(e) => setRelatedTo(e.target.value)}
                  placeholder="e.g., Family Home deed, 2019 Toyota, Life insurance policy"
                  data-testid="related-to-input"
                />
              </div>
            )}
            {mapId === 'physical' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="retention-notes">Retention Notes</Label>
                <Textarea
                  id="retention-notes"
                  value={retentionNotes}
                  onChange={(e) => setRetentionNotes(e.target.value)}
                  placeholder="e.g., Keep forever, Destroy after 7 years, Keep until property sold"
                  className="min-h-[60px] resize-none"
                  data-testid="retention-notes-input"
                />
              </div>
            )}
            {mapId === 'physical' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="digital-backup-exists"
                  checked={digitalBackupExists}
                  onCheckedChange={setDigitalBackupExists}
                  data-testid="digital-backup-exists-checkbox"
                />
                <Label htmlFor="digital-backup-exists" className="text-sm font-normal cursor-pointer">
                  Digital backup exists
                </Label>
              </div>
            )}

            {/* ===================== SOCIAL MEDIA ===================== */}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="platform-name">Platform Name</Label>
                <Input
                  id="platform-name"
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  placeholder="e.g., Facebook, Instagram, LinkedIn, Gmail, Dropbox"
                  data-testid="platform-name-input"
                />
              </div>
            )}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="social-username">Username / Handle</Label>
                <Input
                  id="social-username"
                  value={socialUsername}
                  onChange={(e) => setSocialUsername(e.target.value)}
                  placeholder="e.g. @username"
                  data-testid="social-username-input"
                />
              </div>
            )}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="profile-url">Profile URL</Label>
                <Input
                  id="profile-url"
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                  placeholder="Full URL to profile"
                  data-testid="profile-url-input"
                />
              </div>
            )}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="social-email">Email Associated</Label>
                <Input
                  id="social-email"
                  value={socialEmail}
                  onChange={(e) => setSocialEmail(e.target.value)}
                  placeholder="Email used to register"
                  data-testid="social-email-input"
                />
              </div>
            )}
            {mapId === 'social' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="two-factor-method">Two-Factor Auth Method</Label>
                <Input
                  id="two-factor-method"
                  value={twoFactorMethod}
                  onChange={(e) => setTwoFactorMethod(e.target.value)}
                  placeholder="e.g. Phone, authenticator app, backup codes in..."
                  data-testid="two-factor-method-input"
                />
              </div>
            )}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="account-disposition">What should happen to this account?</Label>
                <Select value={accountDisposition} onValueChange={setAccountDisposition}>
                  <SelectTrigger id="account-disposition" data-testid="account-disposition-select">
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="memorialize">Memorialize (keep visible but locked)</SelectItem>
                    <SelectItem value="archive">Archive / Keep active</SelectItem>
                    <SelectItem value="close">Close / Delete</SelectItem>
                    <SelectItem value="transfer">Transfer to someone</SelectItem>
                    <SelectItem value="other">Other (explain in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'social' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="paid-account"
                  checked={paidAccount}
                  onCheckedChange={setPaidAccount}
                  data-testid="paid-account-checkbox"
                />
                <Label htmlFor="paid-account" className="text-sm font-normal cursor-pointer">
                  Paid / Subscription account
                </Label>
              </div>
            )}
            {mapId === 'social' && paidAccount && (
              <div className="space-y-2">
                <Label htmlFor="social-monthly-cost">Monthly Cost</Label>
                <Input
                  id="social-monthly-cost"
                  value={socialMonthlyCost}
                  onChange={(e) => setSocialMonthlyCost(e.target.value)}
                  placeholder="If paid"
                  data-testid="social-monthly-cost-input"
                />
              </div>
            )}
            {mapId === 'social' && paidAccount && (
              <div className="space-y-2">
                <Label htmlFor="social-auto-renewal-date">Auto-renewal Date</Label>
                <Input
                  id="social-auto-renewal-date"
                  value={socialAutoRenewalDate}
                  onChange={(e) => setSocialAutoRenewalDate(e.target.value)}
                  placeholder="e.g. 15th of each month, 2026-01-01"
                  data-testid="social-auto-renewal-date-input"
                />
              </div>
            )}

            {/* ===================== SUBSCRIPTIONS ===================== */}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="service-type">Service Type</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger id="service-type" data-testid="service-type-select">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="streaming">Streaming</SelectItem>
                    <SelectItem value="music">Music</SelectItem>
                    <SelectItem value="software">Software / App</SelectItem>
                    <SelectItem value="news-magazine">News / Magazine</SelectItem>
                    <SelectItem value="gym-fitness">Gym / Fitness</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="gaming">Gaming</SelectItem>
                    <SelectItem value="cloud-storage">Cloud Storage</SelectItem>
                    <SelectItem value="food-delivery">Food / Delivery</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="sub-provider">Provider / Company</Label>
                <Input
                  id="sub-provider"
                  value={subProvider}
                  onChange={(e) => setSubProvider(e.target.value)}
                  placeholder="e.g. Netflix, Spotify, Adobe"
                  data-testid="sub-provider-input"
                />
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="sub-account-email">Account Email</Label>
                <Input
                  id="sub-account-email"
                  value={subAccountEmail}
                  onChange={(e) => setSubAccountEmail(e.target.value)}
                  placeholder="Email used to register"
                  data-testid="sub-account-email-input"
                />
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="sub-monthly-cost">Monthly Cost</Label>
                <Input
                  id="sub-monthly-cost"
                  value={subMonthlyCost}
                  onChange={(e) => setSubMonthlyCost(e.target.value)}
                  placeholder="e.g. £9.99, $15"
                  data-testid="sub-monthly-cost-input"
                />
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="billing-cycle">Billing Cycle</Label>
                <Select value={billingCycle} onValueChange={setBillingCycle}>
                  <SelectTrigger id="billing-cycle" data-testid="billing-cycle-select">
                    <SelectValue placeholder="Select billing cycle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2">
                <Label htmlFor="payment-method">Payment Method</Label>
                <Input
                  id="payment-method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  placeholder="e.g. Visa ending 4242, PayPal"
                  data-testid="payment-method-input"
                />
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="cancellation-instructions">Cancellation Instructions</Label>
                <Input
                  id="cancellation-instructions"
                  value={cancellationInstructions}
                  onChange={(e) => setCancellationInstructions(e.target.value)}
                  placeholder="e.g. Cancel via website settings"
                  data-testid="cancellation-instructions-input"
                />
              </div>
            )}
            {mapId === 'subscriptions' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sub-auto-renewal"
                  checked={subAutoRenewal}
                  onCheckedChange={setSubAutoRenewal}
                  data-testid="sub-auto-renewal-checkbox"
                />
                <Label htmlFor="sub-auto-renewal" className="text-sm font-normal cursor-pointer">
                  Auto-renewal
                </Label>
              </div>
            )}

            {/* ===================== BUSINESS INTERESTS ===================== */}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="business-type">Business Type</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger id="business-type" data-testid="business-type-select">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="sole-trader">Sole Trader</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="limited-company">Limited Company</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                    <SelectItem value="investment-holding">Investment / Holding</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="business-role">Your Role</Label>
                <Input
                  id="business-role"
                  value={businessRole}
                  onChange={(e) => setBusinessRole(e.target.value)}
                  placeholder="e.g. Director, Partner, Shareholder"
                  data-testid="business-role-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="ownership-percentage">Ownership Percentage</Label>
                <Input
                  id="ownership-percentage"
                  value={ownershipPercentage}
                  onChange={(e) => setOwnershipPercentage(e.target.value)}
                  placeholder="e.g. 100%, 50%"
                  data-testid="ownership-percentage-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="company-reg-number">Company Registration Number</Label>
                <Input
                  id="company-reg-number"
                  value={companyRegNumber}
                  onChange={(e) => setCompanyRegNumber(e.target.value)}
                  placeholder="e.g. 12345678"
                  data-testid="company-reg-number-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="registered-address">Registered Address</Label>
                <Input
                  id="registered-address"
                  value={registeredAddress}
                  onChange={(e) => setRegisteredAddress(e.target.value)}
                  placeholder="e.g. 1 Business Park, London"
                  data-testid="registered-address-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="accountant-contact">Accountant Contact</Label>
                <Input
                  id="accountant-contact"
                  value={accountantContact}
                  onChange={(e) => setAccountantContact(e.target.value)}
                  placeholder="e.g. Smith & Co, 555-1234"
                  data-testid="accountant-contact-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="other-directors">Other Directors / Partners</Label>
                <Input
                  id="other-directors"
                  value={otherDirectors}
                  onChange={(e) => setOtherDirectors(e.target.value)}
                  placeholder="Names and contact details"
                  data-testid="other-directors-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="business-bank">Business Bank</Label>
                <Input
                  id="business-bank"
                  value={businessBank}
                  onChange={(e) => setBusinessBank(e.target.value)}
                  placeholder="e.g. Barclays Business"
                  data-testid="business-bank-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2">
                <Label htmlFor="business-approx-value">Approximate Value</Label>
                <Input
                  id="business-approx-value"
                  value={businessApproxValue}
                  onChange={(e) => setBusinessApproxValue(e.target.value)}
                  placeholder="Rough estimate of business value"
                  data-testid="business-approx-value-input"
                />
              </div>
            )}
            {mapId === 'business' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="key-documents-location">Key Documents Location</Label>
                <Input
                  id="key-documents-location"
                  value={keyDocumentsLocation}
                  onChange={(e) => setKeyDocumentsLocation(e.target.value)}
                  placeholder="Where are articles, certificates stored"
                  data-testid="key-documents-location-input"
                />
              </div>
            )}

            {/* Location / System - generic (hidden for maps with their own location/identity fields) */}
            {!['property', 'vehicles', 'pets', 'liabilities', 'social', 'subscriptions', 'business', 'crypto'].includes(mapId) && (
              <div className="space-y-2">
                <Label htmlFor="entry-location">
                  {mapId === 'shares' ? 'Broker / Platform' : 'Location / System'}
                </Label>
                <Input
                  id="entry-location"
                  value={entryLocation}
                  onChange={(e) => setEntryLocation(e.target.value)}
                  placeholder={mapId === 'shares' ? 'e.g., Vanguard, Fidelity, Interactive Brokers' : 'e.g., First National Bank'}
                  data-testid="entry-location-input"
                />
              </div>
            )}

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="entry-priority">Priority</Label>
              <Select value={entryPriority} onValueChange={setEntryPriority}>
                <SelectTrigger id="entry-priority" data-testid="entry-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      <div className="flex flex-col">
                        <span>{level.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes / Context */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="entry-notes">
                {mapId === 'pets' ? 'Notes / Context (use for additional details)' : 'Notes / Context'}
              </Label>
              <Textarea
                id="entry-notes"
                value={entryNotes}
                onChange={(e) => setEntryNotes(e.target.value)}
                placeholder={mapId === 'pets' ? 'Any additional details about care routine, preferences, quirks...' : 'Any relevant context...'}
                className="min-h-[80px] resize-none"
                data-testid="entry-notes-input"
              />
            </div>

            {/* What someone should do first */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="entry-first-action">What someone should do first (optional)</Label>
              <Input
                id="entry-first-action"
                value={entryFirstAction}
                onChange={(e) => setEntryFirstAction(e.target.value)}
                placeholder={
                  mapId === 'pets' ? 'e.g., Contact sister Jane immediately, she knows the routine' :
                  mapId === 'vehicles' ? 'e.g., Contact insurance company, keys in kitchen drawer' :
                  mapId === 'property' ? 'e.g., Contact solicitor, mortgage statements in filing cabinet' :
                  'e.g., Contact the branch manager'
                }
                data-testid="entry-first-action-input"
              />
            </div>

            {/* Entry-level wishes (Non-binding) */}
            <div className="space-y-3 pt-4 border-t border-border md:col-span-2">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Your Wishes for This Entry (Non-Binding)</Label>
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  These wishes do not replace a legal will or beneficiary designations.
                </p>
              </div>

              <RadioGroup 
                value={wishesOverride ? 'override' : 'default'} 
                onValueChange={(v) => setWishesOverride(v === 'override')}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="default" id="wishes-default" />
                  <Label htmlFor="wishes-default" className="text-sm font-normal cursor-pointer">
                    Use map default
                    {mapWishes?.contactName && (
                      <span className="text-muted-foreground ml-1">
                        ({mapWishes.contactName})
                      </span>
                    )}
                    {!mapWishes?.contactId && !mapWishes?.notes && (
                      <span className="text-muted-foreground ml-1">(not set)</span>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="override" id="wishes-override" />
                  <Label htmlFor="wishes-override" className="text-sm font-normal cursor-pointer">
                    Override for this entry
                  </Label>
                </div>
              </RadioGroup>

              {wishesOverride && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Intended for</Label>
                    <ContactSelector
                      value={entryWishesContactId}
                      onSelect={setEntryWishesContactId}
                      placeholder="Select contact..."
                      data-testid="entry-wishes-contact"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Textarea
                      value={entryWishesNotes}
                      onChange={(e) => setEntryWishesNotes(e.target.value)}
                      placeholder="e.g., Held in trust until age 25..."
                      className="min-h-[60px] resize-none text-sm"
                      data-testid="entry-wishes-notes"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-background">
            <Button variant="ghost" onClick={() => setIsEntryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} data-testid="save-entry-btn">
              {editingEntry ? 'Update Entry' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapDetail;
